/* Sundays in Bed — Free Interior Design Quotation flow.
   One question per screen; contractor-works screen appears only when an estimate is requested.
   Pricing/state keys are code-controlled; copy comes from section settings with code fallbacks
   (fallbacks matter: route-cached HTML may carry an older config for a few hours).
   PII (name/email/phone) lives in memory only and is cleared after success; other answers persist in sessionStorage.
   No RM figure is ever rendered before the contact form is submitted. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) { module.exports = factory(); }
  else { root.SIBQuiz = factory(); }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ---- validators (pure; unit-tested in node) ---- */
  var validators = {
    email: function (v) { return typeof v === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim()); },
    required: function (v) { return typeof v === 'string' ? v.trim().length > 0 : v != null; },
    nonEmptyArray: function (v) { return Array.isArray(v) && v.length > 0; },
    phone: function (v) {
      if (typeof v !== 'string') return false;
      var d = v.replace(/[\s\-().]/g, '');
      return /^\+?\d{7,15}$/.test(d);
    },
    oneOf: function (v, allowed) { return allowed.indexOf(v) >= 0; },
    subsetOf: function (arr, allowed) {
      return Array.isArray(arr) && arr.every(function (v) { return allowed.indexOf(v) >= 0; });
    }
  };

  /* Stable option keys — code-controlled. Labels are presentation only. */
  var KEYS = {
    siteConditions: ['new_developer', 'resale_older'],
    projectPurposes: ['own_stay', 'rental'],
    homeTypes: ['condo', 'terrace', 'semi-d', 'bungalow', 'other'],
    locations: ['kl', 'selangor', 'other'],
    services: ['styling', 'procurement', 'virtual', 'design', 'not-sure'],
    budgetBands: ['rm10-50k', 'rm50-100k', 'rm100-150k', 'rm150-200k', 'rm200-300k', 'rm300-500k', 'rm500k-plus'],
    contractorPrefs: ['include', 'own', 'none', 'unsure'],
    works: ['plumb', 'floor', 'paint', 'elec', 'kit', 'ward', 'ffe', 'wet'],
    tiers: ['e', 'p', 's']
  };
  /* Rental defaults to these two services (pre-selected, not locked) the moment "Rental" is
     chosen — only when services are still untouched, so it never clobbers a manual edit made
     before revisiting this question. */
  var RENTAL_DEFAULT_SERVICES = ['styling', 'procurement'];
  /* 'not-sure' clears every other service and vice versa */
  var EXCLUSIVE_SERVICE = 'not-sure';

  /* Contractor works only exist for services that put someone on site. Interior Styling
     restyles what the client already owns, and Procurement Service buys and delivers it —
     neither involves building work, so asking whether to estimate renovation cost is a dead
     question, and any answer produces a renovation figure we could never quote against.
     Interior Design, Virtual Design and "Not sure" can all lead to works, so those still ask.
     Empty selection returns true: services is answered before this step, and hiding a screen
     off a not-yet-made choice is worse than showing one that proves unnecessary.

     Declared here, with the other shared constants, rather than beside the prototype method
     that uses it: the Node export below returns before SibQuiz is defined, so anything the
     test-suite needs must already hold a value by this point. */
  var RENOVATION_CAPABLE_SERVICES = ['design', 'virtual', 'not-sure'];
  function contractorAppliesTo(services) {
    if (!services || !services.length) return true;
    return services.filter(function (v) { return RENOVATION_CAPABLE_SERVICES.indexOf(v) >= 0; }).length > 0;
  }

  /* Code-side option labels (fallbacks; cfg lists override only when their keys match KEYS) */
  /* UI home-type key -> BOQ engine's 01_Project property-type string (SITE_FACTORS lookup) */
  var HOME_TYPE_TO_PROPERTY_TYPE = {
    condo: 'Condominium', terrace: 'Terrace', 'semi-d': 'Semi-D', bungalow: 'Bungalow', other: 'Terrace'
  };

  var OPTS = {
    siteConditions: [
      { value: 'new_developer', label: 'New unit' },
      { value: 'resale_older', label: 'Resale / older property' }
    ],
    projectPurposes: [
      { value: 'own_stay', label: 'Own stay' },
      { value: 'rental', label: 'Rental' }
    ],
    homeTypes: [
      { value: 'condo', label: 'Condominium / Apartment' },
      { value: 'terrace', label: 'Terrace' },
      { value: 'semi-d', label: 'Semi-D' },
      { value: 'bungalow', label: 'Bungalow' },
      { value: 'other', label: 'Other' }
    ],
    locations: [
      { value: 'kl', label: 'Kuala Lumpur' },
      { value: 'selangor', label: 'Selangor' },
      { value: 'other', label: 'Others' }
    ],
    services: [
      { value: 'styling', label: 'Interior Styling', desc: 'We restyle each room with what you own.' },
      { value: 'procurement', label: 'Procurement Service', desc: 'We buy everything for you — ordering, deliveries, returns.' },
      { value: 'virtual', label: 'Virtual Design', desc: 'Design over video, from your floor plan — from anywhere.' },
      { value: 'design', label: 'Interior Design', desc: 'Everything from design to move-in. Concierge included.' },
      { value: 'not-sure', label: 'Not sure', desc: 'Help me figure out what my home needs.' }
    ],
    budgetBands: [
      { value: 'rm10-50k', label: 'RM10,000–50,000' },
      { value: 'rm50-100k', label: 'RM50,000–100,000' },
      { value: 'rm100-150k', label: 'RM100,000–150,000' },
      { value: 'rm150-200k', label: 'RM150,000–200,000' },
      { value: 'rm200-300k', label: 'RM200,000–300,000' },
      { value: 'rm300-500k', label: 'RM300,000–500,000' },
      { value: 'rm500k-plus', label: 'More than RM500,000' }
    ],
    contractorPrefs: [
      { value: 'include', label: 'Yes, include an estimate' },
      { value: 'own', label: 'I have my own contractor' },
      { value: 'none', label: 'I don’t need renovation works' },
      { value: 'unsure', label: 'I’m not sure yet' }
    ],
    /* Display labels only — value stays 'e'/'p'/'s' and desc is untouched. The BOQ engine's
       own rate-card vocabulary ('Essential'/'Premium'/'Signature') is separate from this and
       must never be renamed here — see TIER_ENGINE_NAME below. */
    tiers: [
      { value: 'e', label: 'Essential', desc: ['Melamine carpentry', 'Quartz top', 'Existing ceiling', 'Locally sourced'] },
      { value: 'p', label: 'Curated', desc: ['Elevated melamine + laminate', 'Sintered stone', 'Plaster ceiling', 'Local + imported brands'] },
      { value: 's', label: 'Bespoke', desc: ['Bespoke laminate', 'Detailed joinery', 'Ceiling with cornice', 'Imported brands'] }
    ],
    works: [
      { value: 'plumb', label: 'Plumbing works' },
      { value: 'floor', label: 'Flooring change' },
      { value: 'paint', label: 'Painting & ceiling' },
      { value: 'elec', label: 'Lighting & wiring' },
      { value: 'kit', label: 'Kitchen carpentry' },
      { value: 'ward', label: 'Built-in wardrobe' },
      { value: 'ffe', label: 'Furniture, fixtures & equipment (FF&E)' },
      { value: 'wet', label: 'Wet-area tiling (bathroom & kitchen)' }
    ]
  };
  /* HTML-escape for the few places that build markup by string (report figures, budget-fit
     note). Module-level so prototype methods outside showReport can use the same one. */
  function esc(t) { return String(t == null ? '' : t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); }
  function optLabel(list, val) {
    var hit = list.filter(function (o) { return o.value === val; })[0];
    return hit ? hit.label : (val || '—');
  }

  /* UI tier letter -> BOQ engine's own tier vocabulary. This must always stay 'Essential'/
     'Premium'/'Signature' — it's the literal rate-card column name the engine string-compares
     against (sib-boq-engine.js resolveBandRate etc.), independent of whatever the customer
     sees on screen. Never feed a display label into finishTier. */
  var TIER_ENGINE_NAME = { e: 'Essential', p: 'Premium', s: 'Signature' };
  /* UI tier letter -> customer-facing tier name (report, both emails). v7 merged this with
     the engine map above into one, since the v2/v6 engine's separate 'Budget'/'Mid'/'High'
     internal codenames had leaked into customer-facing text as literal "Budget"; re-split back
     into two maps to rename the display tiers (Essential/Curated/Bespoke) without touching the
     engine's own vocabulary — every consumer of a *display* tier name must read TIER_LABEL,
     every consumer feeding the BOQ engine must read TIER_ENGINE_NAME, never the other one. */
  var TIER_LABEL = { e: 'Essential', p: 'Curated', s: 'Bespoke' };
  /* Tier order, cheapest first — used to walk up/down when suggesting an alternative. */
  var TIER_ORDER = ['e', 'p', 's'];
  /* Numeric equivalents of the budgetBands options, for comparing the estimate against what
     the customer said they planned to invest. 'rm500k-plus' is open-ended (null high), so it
     can never read as over budget. Keep in step with OPTS.budgetBands. */
  var BUDGET_RANGE = {
    'rm10-50k': [10000, 50000],
    'rm50-100k': [50000, 100000],
    'rm100-150k': [100000, 150000],
    'rm150-200k': [150000, 200000],
    'rm200-300k': [200000, 300000],
    'rm300-500k': [300000, 500000],
    'rm500k-plus': [500000, null]
  };
  /* The band labels slot into "...inside your ___ range", which reads wrong for the open-ended
     band ("your More than RM500,000 range"). Override just that one with a natural phrase. */
  var BUDGET_PHRASE = { 'rm500k-plus': 'the RM500,000+ you had in mind' };
  function budgetPhrase(bandKey, label) {
    /* raw text — escaping happens once at render (segsToHtml), so this must NOT pre-escape */
    return BUDGET_PHRASE[bandKey] || ('your ' + label + ' budget');
  }

  /* ---- stored-draft id reuse (pure; unit-tested in node) ----
     How long a stored submissionId stays reusable. Refreshed on every answer via persist(), so an
     actively-filled quiz never expires mid-flow — this only bounds ABANDONED drafts. */
  var ID_REUSE_MAX_MS = 30 * 60 * 1000;
  /* One past the last real screen index. Must match QUIZ_STEPS in worker.js, which validates
     stepKey against its own list and silently discards anything it does not recognise. */
  var QUIZ_SUBMITTED_STEP = 13;
  /* GA4 conversion dedupe. generate_lead fires from finish(), which is now reachable more than
     once for a single lead: a worker success, a contact-form rescue after a success the browser
     could not read, and a replayed submit all converge there. Double-counting inflates the
     conversion number, corrupts cost-per-lead, and — if generate_lead is a Key Event feeding
     Google Ads bidding — makes the bidding optimise against fiction. transaction_id is what GA4
     itself dedupes on; the local ring is the real defence, since it survives a reload. Ids only,
     never PII, and capped so it cannot grow without bound. */
  var TRACKED_KEY = 'sib-quiz-tracked', TRACKED_MAX = 20;
  function alreadyTracked(id) {
    if (!id) return false;
    try { return (JSON.parse(localStorage.getItem(TRACKED_KEY) || '[]') || []).indexOf(id) !== -1; }
    catch (e) { return false; }
  }
  function markTracked(id) {
    if (!id) return;
    try {
      var list = JSON.parse(localStorage.getItem(TRACKED_KEY) || '[]') || [];
      list.push(id);
      localStorage.setItem(TRACKED_KEY, JSON.stringify(list.slice(-TRACKED_MAX)));
    } catch (e) { /* private mode / quota — dedupe degrades, tracking still works */ }
  }
  /* Reuse a stored submissionId only while it is demonstrably fresh. Anything older is either a
     spent id (one that predates the persist() guard) or an abandoned draft whose worker-side
     idempotency marker may still be live — the worker remembers ids for 24h, and submitting under
     either is answered {ok:true,duplicate:true} and silently dropped. Entries written before
     savedAt existed return false, so sessions currently holding a spent id self-heal on next load.
     A negative age (clock moved backwards) is stale too: a fresh id costs nothing, a reused stale
     one costs the entire lead. See LEAD-PIPELINE-AUDIT.md P0-2. */
  function idStillReusable(saved, now) {
    if (!saved || !saved.submissionId) return false;
    if (typeof saved.savedAt !== 'number') return false;
    var age = now - saved.savedAt;
    return age >= 0 && age < ID_REUSE_MAX_MS;
  }

  if (typeof window === 'undefined' || typeof HTMLElement === 'undefined') {
    /* Exported for the Node test-suite so it exercises the SHIPPED implementations rather than
     a mirror that can silently drift out of step with them. */
  return { validators: validators, KEYS: KEYS, OPTS: OPTS,
    idStillReusable: idStillReusable, ID_REUSE_MAX_MS: ID_REUSE_MAX_MS,
    contractorAppliesTo: contractorAppliesTo, RENOVATION_CAPABLE_SERVICES: RENOVATION_CAPABLE_SERVICES,
    segsToHtml: segsToHtml, beatsToHtml: beatsToHtml, beatsToText: beatsToText,
    BUDGET_RANGE: BUDGET_RANGE, budgetPhrase: budgetPhrase,
    TIER_LABEL: TIER_LABEL, TIER_ENGINE_NAME: TIER_ENGINE_NAME, TIER_ORDER: TIER_ORDER };
  }

  /* ---- attribution (first-touch, session-scoped) ----
     Captured the FIRST time a visitor reaches any quiz-bearing page and held for the session, so
     someone who arrives on an ad, reads two articles and submits later is still credited to the
     ad rather than to whatever page they were last on. Stored separately from STORE_KEY because
     it must survive the post-submit clear that wipes the answers.
     Contains no PII: campaign parameters, landing path and referrer host only. */
  var ATTR_KEY = 'sib-attr-v1';
  function readAttribution() {
    try {
      var saved = JSON.parse(sessionStorage.getItem(ATTR_KEY) || 'null');
      if (saved && saved.source) return saved;
    } catch (e) {}

    var qs, src = '', ref = '', host = '';
    try { qs = new URLSearchParams(location.search); } catch (e) { qs = null; }
    var get = function (k) { try { return (qs && qs.get(k)) || ''; } catch (e) { return ''; } };

    /* Most specific wins. gclid/fbclid are present on ad clicks even when the utm tags are
       stripped by a redirect, so they are the more reliable paid signal of the two. */
    var utmSource = get('utm_source'), utmMedium = get('utm_medium');
    if (get('gclid')) src = 'google-ads';
    else if (get('fbclid')) src = 'meta-ads';
    else if (utmSource) src = (utmSource + (utmMedium ? '/' + utmMedium : '')).toLowerCase();
    else {
      try { ref = document.referrer || ''; host = ref ? new URL(ref).hostname.replace(/^www\./, '') : ''; } catch (e) { host = ''; }
      if (!host) src = 'direct';
      else if (/mysundaysinbed\.com$/.test(host)) src = 'internal';
      else if (/google\.|bing\.|duckduckgo\.|yahoo\./.test(host)) src = 'organic-search';
      else if (/instagram\.|facebook\.|tiktok\.|pinterest\.|linkedin\./.test(host)) src = 'social';
      else src = 'referral/' + host;
    }

    var attr = {
      source: src.slice(0, 60),
      landingPage: (location.pathname + location.search).slice(0, 300),
      referrer: (ref || '').slice(0, 300),
      campaign: (get('utm_campaign') || '').slice(0, 60)
    };
    /* 'internal' is a same-site hop, not an origin — never let it overwrite a real first touch. */
    try {
      if (attr.source !== 'internal') sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    } catch (e) {}
    return attr;
  }

  var STORE_KEY = 'sib-quiz-v2'; /* non-PII answers only (v2: new question model) */

  /* Carries the finished lead forward to /pages/contact so the booking step never re-asks for
     anything already given. sessionStorage (same-origin, dies with the tab) — PII must never
     travel in a query string. Written once at submit success, read once and cleared by the
     contact page. HANDOFF_TTL keeps a stale tab from greeting a different visitor by name. */
  var HANDOFF_KEY = 'sib-quiz-handoff', HANDOFF_TTL = 60 * 60 * 1000;

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  /* finish-tier spec list — short phrases, one per line with a subtle dot, rather than a
     single "•"-joined run of text that wraps awkwardly once a tier has 4 items. */
  function tierDescHtml(desc) {
    var items = Array.isArray(desc) ? desc : [desc];
    return '<ul class="sq-tierlist">' + items.map(function (d) { return '<li>' + d + '</li>'; }).join('') + '</ul>';
  }
  /* --- advisor message model -------------------------------------------------------------
     A message is an array of "beats" (paragraphs); each beat is an array of {t, b} segments,
     where b marks emphasis. Keeping it as data rather than an HTML string is what lets the
     same message be (a) streamed character by character with correct markup mid-word,
     (b) rendered instantly on a later re-calculation, and (c) flattened to plain text for
     screen readers — without three copies of the copy drifting apart. */
  function segsToHtml(segs) {
    return segs.map(function (s) { return s.b ? '<b>' + esc(s.t) + '</b>' : esc(s.t); }).join('');
  }
  function beatsToHtml(beats) {
    return beats.map(function (segs) { return '<p>' + segsToHtml(segs) + '</p>'; }).join('');
  }
  function beatsToText(beats) {
    return beats.map(function (segs) {
      return segs.map(function (s) { return s.t; }).join('');
    }).join(' ');
  }
  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    /* This id is what the worker's database keys on to decide "have I already stored this
       submission", so a predictable one is not merely untidy — two visitors colliding would put
       one of them into the other's row. Date.now() plus Math.random is both guessable and
       low-entropy. Draw real random bytes wherever the browser has them; the old string survives
       only for a browser with no crypto object at all. */
    if (window.crypto && crypto.getRandomValues) {
      var b = new Uint8Array(16), hex = '';
      crypto.getRandomValues(b);
      for (var i = 0; i < b.length; i++) hex += (b[i] + 0x100).toString(16).slice(1);
      return 'sib-' + hex;
    }
    return 'sib-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  var SibQuiz = function () { return Reflect.construct(HTMLElement, [], SibQuiz); };
  SibQuiz.prototype = Object.create(HTMLElement.prototype);

  /* All flow-specific styles ship with this asset (not the section stylesheet) so they
     reach storefront paths whose HTML snapshot is still route-cached. */
  var FLOW_CSS = [
    /* split layout: question pinned left, options right (rooms screen) */
    '.sib .sq-screen.sq-split{display:grid;grid-template-columns:minmax(250px,.75fr) minmax(0,1.5fr);gap:10px 60px;align-items:start;}',
    '.sib .sq-split .sq-head{position:sticky;top:130px;text-align:left;}',
    '.sib .sq-split .sq-head .sq-h,.sib .sq-split .sq-head .sq-sub{text-align:left;margin-left:0;margin-right:0;}',
    '.sib .sq-split>.sq-nav{grid-column:1/-1;}',
    '@media(max-width:899px){.sib .sq-screen.sq-split{display:block;}.sib .sq-split .sq-head{position:static;}}',
    /* room circles: line icon + label inside, stepper below */
    '.sib .sq-roomgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(128px,1fr));gap:28px 14px;justify-items:center;margin-top:6px;}',
    '.sib .sq-roomitem{text-align:center;width:100%;}',
    '.sib .sq-roomcircle{position:relative;width:min(132px,92%);aspect-ratio:1;border-radius:50%;margin:0 auto;cursor:pointer;background:#F3EDE2;border:none;padding:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:7px;transition:box-shadow .25s ease,transform .25s ease,background .25s ease;}',
    '.sib .sq-roomcircle svg{width:38px;height:38px;stroke:#33291D;fill:none;stroke-width:1.6;stroke-linecap:round;stroke-linejoin:round;}',
    '.sib .sq-roomcircle:hover{transform:translateY(-2px);box-shadow:0 6px 18px rgba(51,41,29,.12);}',
    '.sib .sq-roomcircle[aria-pressed=true]{background:#F2D9AE;}',
    '.sib .sq-roomcheck{position:absolute;top:4px;right:8px;z-index:2;width:24px;height:24px;border-radius:50%;background:#FDFBF6;border:1px solid #E2D8C6;display:flex;align-items:center;justify-content:center;font-size:12px;color:#33291D;}',
    '.sib .sq-roominfo{margin-top:8px;position:relative;display:inline-block;}',
    '.sib .sq-roominfo summary{list-style:none;font-size:11.5px;color:#8A7B66;cursor:pointer;text-decoration:underline;text-underline-offset:3px;text-decoration-color:#C9BBA0;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-roominfo summary::-webkit-details-marker{display:none;}',
    '.sib .sq-roominfo summary:hover,.sib .sq-roominfo summary:focus-visible{color:#9C7434;text-decoration-color:#9C7434;}',
    '.sib .sq-roomtip{position:absolute;top:calc(100% + 8px);left:50%;transform:translateX(-50%);width:min(240px,80vw);background:#332A1F;color:#EFE7D8;font-size:12px;line-height:1.6;padding:12px 14px;z-index:60;box-shadow:0 12px 30px rgba(51,41,29,.22);text-align:left;display:none;}',
    '.sib .sq-roomtip::after{content:"";position:absolute;bottom:100%;left:50%;transform:translateX(-50%);border:6px solid transparent;border-bottom-color:#332A1F;}',
    '.sib .sq-roominfo[open] .sq-roomtip{display:block;}',
    '@media(hover:hover){.sib .sq-roominfo:hover .sq-roomtip{display:block;}}',
    '.sib .sq-roomlabelin{font-size:12.5px;line-height:1.3;color:#33291D;max-width:84%;text-align:center;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-roomstep{display:flex;align-items:center;justify-content:center;gap:12px;margin-top:10px;}',
    '.sib .sq-roomstep button{width:26px;height:26px;border-radius:50%;border:none;background:#F5E3CB;color:#33291D;font-size:15px;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;}',
    '.sib .sq-roomstep button:hover{background:#EDD3B1;}',
    '.sib .sq-roomcount{font-size:14px;color:#33291D;min-width:14px;text-align:center;font-family:"Work Sans",sans-serif;}',
    /* question headings */
    '.sib .sq-h{font-size:clamp(21px,2.7vw,30px);}',
    /* built-up-area slider (same UI as the cost calculator) */
    '.sib .sq-sliderwrap{max-width:620px;margin:26px auto 4px;text-align:left;}',
    '.sib .sq-sliderrow{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px;}',
    '.sib .sq-sllabel{font-size:13px;color:#6F6252;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-slout{font-size:16px;font-weight:500;color:#33291D;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-sliderwrap input[type=range]{width:100%;accent-color:#33291D;}',
    /* multi-select service cards reuse .sq-card; pressed state */
    '.sib .sq-card[aria-pressed=true]{border:2px solid #33291D;padding:15px;}',
    /* works screen: finish cards + checkbox rows (same look as the calculator) */
    '.sib .sq-lbl{font-size:11px;letter-spacing:.16em;text-transform:uppercase;color:#6F6252;margin:26px 0 10px;text-align:left;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-tiergrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;}',
    '.sib .sq-tier{border:1px solid #E2D8C6;background:#FDFAF4;padding:16px;cursor:pointer;text-align:left;color:#33291D;width:100%;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-tier.on{border:2px solid #33291D;padding:15px;}',
    '.sib .sq-tier b{font-size:16px;font-weight:500;display:block;margin-bottom:6px;}',
    '.sib .sq-tier span{font-size:14px;color:#6F6252;line-height:1.6;display:block;}',
    '.sib .sq-tierlist{list-style:none;margin:0;padding:0;font-size:14px;color:#6F6252;line-height:1.45;}',
    '.sib .sq-tierlist li{position:relative;padding-left:13px;margin-bottom:4px;}',
    '.sib .sq-tierlist li:last-child{margin-bottom:0;}',
    '.sib .sq-tierlist li::before{content:"";position:absolute;left:0;top:.55em;width:4px;height:4px;border-radius:50%;background:#B9A88C;}',
    '.sib .sq-scpgrid{display:grid;grid-template-columns:repeat(auto-fit,minmax(240px,1fr));gap:8px;}',
    '.sib .sq-scp{display:flex;align-items:center;gap:12px;padding:13px 16px;background:#FDFAF4;border:1px solid #E2D8C6;cursor:pointer;font-size:15px;text-align:left;font-family:"Work Sans",sans-serif;color:#33291D;}',
    '.sib .sq-scp input[type=checkbox]{width:16px;height:16px;accent-color:#33291D;margin:0;flex-shrink:0;}',
    '.sib .sq-worksintro{font-size:13.5px;line-height:1.75;color:#6F6252;max-width:620px;margin:0 auto;}',
    /* nav extras */
    '.sib .sq-next:disabled{opacity:.45;cursor:not-allowed;transform:none;}',
    '.sib .sq-total{font-size:13px;color:#6F6252;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-nav.sq-navcenter{justify-content:center;}',
    /* read-only report — mirrors /pages/cost-calculator's answer panel */
    '.sib .sq-report{max-width:860px;margin:0 auto;text-align:left;}',
    '.sib .sq-repsum{background:#FDFAF4;border:1px solid #E2D8C6;padding:26px 24px;}',
    '.sib .sq-repsum h3{font-family:"Newsreader",Georgia,serif;font-weight:400;font-size:22px;margin:0 0 14px;color:#33291D;}',
    '.sib .sq-reprow{display:flex;justify-content:space-between;align-items:baseline;gap:18px;padding:9px 0;border-bottom:1px solid #EFE7D8;}',
    '.sib .sq-reprow:last-child{border-bottom:none;}',
    '.sib .sq-replbl{font-size:12px;letter-spacing:.1em;text-transform:uppercase;color:#6F6252;flex-shrink:0;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-repval{font-size:14.5px;color:#33291D;text-align:right;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-repans{background:#33291D;padding:36px 24px;margin-top:16px;}',
    '.sib .sq-repans .sq-ansrow{display:flex;justify-content:space-between;align-items:baseline;gap:12px;flex-wrap:wrap;padding:6px 0;}',
    '.sib .sq-repans .sq-anslbl{font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:#A6957C;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-repans .sq-ansval{font-family:"Newsreader",Georgia,serif;font-size:26px;color:#F8F3EB;}',
    '.sib .sq-repans .sq-anssep{border-bottom:1px solid #4A4234;padding-bottom:16px;margin-bottom:16px;}',
    '.sib .sq-repans .sq-ansplus{text-align:center;font-family:"Newsreader",Georgia,serif;font-size:15px;line-height:1;color:#8A7B66;padding:2px 0;}',
    '.sib .sq-repans .sq-anstotal{font-family:"Newsreader",Georgia,serif;font-size:34px;color:#F8F3EB;}',
    '.sib .sq-repans .sq-anscta{display:inline-block;padding:16px 30px;font-size:12px;letter-spacing:.16em;text-transform:uppercase;text-decoration:none;font-family:"Work Sans",sans-serif;font-weight:500;border:1px solid #F8F3EB;background:#F8F3EB;color:#33291D;}',
    '.sib .sq-repans .sq-ansdisc{font-size:12.5px;line-height:1.7;color:#7A6F5D;margin:18px 0 0;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-repans .sq-ansdisc strong{font-weight:500;color:#A6957C;}',
    '.sib .sq-repstatus{text-align:center;font-size:14px;color:#5C7150;margin:0 0 18px;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-repedit{max-width:860px;margin:16px auto 0;text-align:left;}',
    /* Was a single flat sentence inside a tinted box, which read as fine print and got
       skimmed past — the one instruction that makes the whole panel interactive. Now a real
       heading with subcopy, and no box: the hierarchy does the work the border was failing at. */
    '.sib .sq-edithead{margin:0 0 24px;padding-top:26px;border-top:1px solid #E2D8C6;}',
    '.sib .sq-edittitle{font-family:"Newsreader",Georgia,serif;font-weight:400;font-size:clamp(20px,2.3vw,25px);line-height:1.25;letter-spacing:-.01em;margin:0 0 7px;color:#33291D;}',
    '.sib .sq-editsub{font-size:13.5px;line-height:1.7;color:#6F6252;font-family:"Work Sans",sans-serif;margin:0;max-width:50em;}',
    /* Advisor — a short spoken-style read-out of the figures above, streamed in token by token
       when the money panel scrolls into view, ending with the CTA easing in.
       Deliberately LEFT-aligned and width-capped rather than full-bleed: left is the chat
       convention for an incoming message (right would read as the customer's own words), and a
       ~38em cap keeps line length in the readable 65-75 character range instead of running the
       full width of the dark panel. */
    '.sib .sq-adv{margin:26px 0 8px;}',
    '.sib .sq-advhead{display:flex;align-items:center;gap:10px;margin:0 0 12px;opacity:0;transform:translateY(8px);transition:opacity .5s ease,transform .5s cubic-bezier(.22,.61,.36,1);}',
    '.sib .sq-adv.in .sq-advhead{opacity:1;transform:none;}',
    '.sib .sq-advname{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:#A6957C;font-family:"Work Sans",sans-serif;}',
    '.sib .sq-advwave{display:flex;align-items:center;justify-content:center;gap:3px;width:34px;height:34px;border-radius:50%;background:rgba(248,243,235,.09);flex:0 0 auto;}',
    '.sib .sq-advwave i{display:block;width:2.5px;height:6px;border-radius:2px;background:#CBB994;transform-origin:center;transition:opacity .4s ease;}',
    '.sib .sq-adv.talking .sq-advwave i{animation:sqWave 1.15s ease-in-out infinite;}',
    '.sib .sq-adv:not(.talking) .sq-advwave i{opacity:.45;}',
    '.sib .sq-advwave i:nth-child(1){animation-delay:0s;}.sib .sq-advwave i:nth-child(2){animation-delay:.13s;}.sib .sq-advwave i:nth-child(3){animation-delay:.26s;}.sib .sq-advwave i:nth-child(4){animation-delay:.39s;}.sib .sq-advwave i:nth-child(5){animation-delay:.52s;}',
    '@keyframes sqWave{0%,100%{transform:scaleY(1);}50%{transform:scaleY(2.6);}}',
    '.sib .sq-advbub{background:rgba(248,243,235,.085);border:1px solid rgba(248,243,235,.07);border-radius:20px;padding:18px 22px;font-size:13.5px;line-height:1.72;color:#EFE4D2;font-family:"Work Sans",sans-serif;}',
    /* Each paragraph fades in on its own short delay, so the message arrives row by row
       rather than all at once. The first row is held back deliberately: the waveform pulses on
       its own for a beat first, so the message reads as being composed in response to the
       figures above rather than having been sitting there all along. */
    '.sib .sq-advbub p{margin:0 0 11px;opacity:0;transform:translateY(6px);transition:opacity .5s ease,transform .5s cubic-bezier(.22,.61,.36,1);}',
    '.sib .sq-advbub p:last-child{margin-bottom:0;}',
    '.sib .sq-adv.in .sq-advbub p{opacity:1;transform:none;}',
    '.sib .sq-adv.in .sq-advbub p:nth-child(1){transition-delay:.75s;}',
    '.sib .sq-adv.in .sq-advbub p:nth-child(2){transition-delay:1.3s;}',
    '.sib .sq-adv.in .sq-advbub p:nth-child(3){transition-delay:1.85s;}',
    '.sib .sq-advbub b{font-weight:500;font-style:italic;color:#F8F3EB;}',
    /* Full message, visually hidden. The animated bubble is aria-hidden, so assistive tech gets
       the complete text at once instead of a half-streamed fragment being re-announced. */
    '.sib .sq-advsr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;}',
    /* CTA is VISIBLE BY DEFAULT and only hidden while the advisor is actually mid-sentence.
       Inverting it this way makes it fail-safe: any path that never runs — observer never
       fires, stream throws, JS bails after render — leaves the button on screen rather than
       invisible. The animation can only ever be an enhancement, never a gate. */
    '.sib .sq-advcta{margin-top:30px;transition:opacity .55s ease,transform .55s cubic-bezier(.34,1.4,.64,1);}',
    '.sib .sq-advcta.pending{opacity:0;transform:translateY(10px) scale(.98);}',
    '@media(prefers-reduced-motion:reduce){.sib .sq-roomcircle{transition:none;}.sib .sq-roomcircle:hover{transform:none;box-shadow:none;}',
    '.sib .sq-advhead,.sib .sq-advbub,.sib .sq-advcta,.sib .sq-advcta.pending{transition:none;opacity:1;transform:none;}',
    '.sib .sq-advbub p{opacity:1;transform:none;transition:none;}',
    '.sib .sq-adv.talking .sq-advwave i{animation:none;}}'
  ].join('');

  /* Line icons per engine room key — drawn for a Malaysian home */
  var svgIcon = function (paths) { return '<svg viewBox="0 0 48 48" aria-hidden="true">' + paths + '</svg>'; };
  var ICONS = {
    bed: svgIcon('<path d="M8 40V16M40 40V30M8 30h28a4 4 0 0 1 4 4M8 36h32M12 30v-6h10v6"/>'),
    liv: svgIcon('<path d="M12 26v-6a4 4 0 0 1 4-4h16a4 4 0 0 1 4 4v6M6 29a3 3 0 0 1 6 0v5h24v-5a3 3 0 0 1 6 0v7a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2zM13 38v3M35 38v3"/>'),
    din: svgIcon('<path d="M24 7v5M20 16l4-4 4 4M10 27h28M14 27v12M34 27v12M24 27v6"/>'),
    kit: svgIcon('<rect x="10" y="18" width="28" height="22" rx="2"/><path d="M10 26h28M17 18v-4M24 18v-4M31 18v-4M20 32h8"/>'),
    /* Wet kitchen — sink and tap over a run of counter, to read distinctly from the dry
       kitchen's cabinet-and-hob mark beside it. */
    wetkit: svgIcon('<path d="M8 24h32M12 24v14a2 2 0 0 0 2 2h20a2 2 0 0 0 2-2V24"/><path d="M24 24v-6a4 4 0 0 1 4-4h4v5"/><path d="M18 30h12v6H18z"/>'),
    off: svgIcon('<path d="M6 22h36M9 22v18M39 22v18M26 22v11h13M29 27h7M14 22v-6h10v6"/>'),
    bal: svgIcon('<path d="M8 28h32M8 28v12M16 28v12M24 28v12M32 28v12M40 28v12M6 40h36M24 22v-5M24 17c-3 0-6-2-6-6 4 0 6 2 6 6zm0 0c3 0 6-2 6-6-4 0-6 2-6 6z"/>'),
    opn: svgIcon('<path d="M6 33v-5a2 2 0 0 1 4 0v1h10v-1a2 2 0 0 1 4 0v5zM8 33v4M22 33v4M31 24h12M32 24v13M42 24v13M28 37h16"/>'),
    bath: svgIcon('<path d="M10 24v-6a4 4 0 0 1 4-4h1M8 24h32a2 2 0 0 1 2 2v2a8 8 0 0 1-8 8H14a8 8 0 0 1-8-8v-2a2 2 0 0 1 2-2zM16 40v2M32 40v2"/>'),
    util: svgIcon('<rect x="10" y="8" width="28" height="32" rx="3"/><circle cx="24" cy="26" r="9"/><circle cx="24" cy="26" r="4"/><circle cx="15" cy="13" r="1"/><circle cx="20" cy="13" r="1"/>'),
    foyer: svgIcon('<path d="M17 40V10h14v30M31 26h-3M10 40h28M14 40v-4M34 40v-4"/>'),
    oth: svgIcon('<path d="M18 8h12l3 12H15zM24 20v18M17 40h14M24 38v2"/>')
  };

  if (typeof document !== 'undefined' && !document.getElementById('sib-quiz-room-css')) {
    var flowStyle = document.createElement('style');
    flowStyle.id = 'sib-quiz-room-css';
    flowStyle.textContent = FLOW_CSS;
    document.head.appendChild(flowStyle);
  }

  SibQuiz.prototype.connectedCallback = function () {
    var q = this;
    q.cfg = JSON.parse(q.querySelector('[data-quiz-config]').textContent);
    q.E = window.SIBEstimator;
    q.answers = {
      existingSiteCondition: '', projectPurpose: '',
      homeType: '', sqft: 1000, projectRooms: [], roomCounts: {}, propertyName: '',
      location: '', services: [], budgetBand: '', contractorPref: '',
      tier: 'p', works: ['paint', 'kit', 'ward']
    };
    q.pii = { name: '', email: '', phone: '', marketingOptIn: false };
    q.submissionId = uuid();
    q.submitting = false; q.submitted = false;
    try {
      var saved = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null');
      if (saved && saved.answers) {
        q.answers = Object.assign(q.answers, saved.answers);
        /* Reuse a stored submissionId only while it is demonstrably fresh. Anything older is either a
           spent id that predates the persist() guard, or an abandoned draft whose worker-side
           idempotency marker may still be live (the worker remembers ids for 24h) — submitting under
           either is answered as a duplicate and silently dropped. Entries written before this change
           carry no savedAt, so they read as stale and get a fresh id: existing poisoned sessions
           self-heal. A fresh id costs nothing; a reused stale one costs the entire lead. */
        if (idStillReusable(saved, Date.now())) q.submissionId = saved.submissionId;
      }
    } catch (e) {}
    /* sanitise restored answers against current keys */
    if (!q.answers.roomCounts) q.answers.roomCounts = {};
    q.answers.projectRooms = (q.answers.projectRooms || []).filter(function (k) { return q.E.ROOMS[k]; });
    q.answers.projectRooms.forEach(function (k) { if (!q.answers.roomCounts[k]) q.answers.roomCounts[k] = 1; });
    if (KEYS.siteConditions.indexOf(q.answers.existingSiteCondition) < 0) q.answers.existingSiteCondition = '';
    if (KEYS.projectPurposes.indexOf(q.answers.projectPurpose) < 0) q.answers.projectPurpose = '';
    if (KEYS.homeTypes.indexOf(q.answers.homeType) < 0) q.answers.homeType = '';
    if (KEYS.budgetBands.indexOf(q.answers.budgetBand) < 0) q.answers.budgetBand = '';
    if (KEYS.contractorPrefs.indexOf(q.answers.contractorPref) < 0) q.answers.contractorPref = '';
    q.answers.services = (q.answers.services || []).filter(function (s) { return KEYS.services.indexOf(s) >= 0; });
    if (KEYS.tiers.indexOf(q.answers.tier) < 0) q.answers.tier = 'p';
    q.answers.works = (q.answers.works || []).filter(function (w) { return KEYS.works.indexOf(w) >= 0; });
    q.answers.sqft = q.E.clampSqft(q.answers.sqft);

    q.screens = q.buildScreenList();
    q.idx = 0;
    q.root = q.querySelector('[data-quiz-root]');
    q.progressBar = q.querySelector('[data-quiz-progress]');
    q.liveRegion = q.querySelector('[data-quiz-live]');

    var params = new URLSearchParams(location.search);
    var landingEls = document.querySelectorAll('[data-quiz-landing]');
    var start = function (push) {
      landingEls.forEach(function (el) { el.style.display = 'none'; });
      q.style.display = 'block';
      q.render();
      q.trackStep();
      if (push) history.replaceState(null, '', location.pathname + '?start=1');
      q.root.focus({ preventScroll: false });
      window.scrollTo({ top: 0 });
    };
    q.startQuiz = start;
    if (params.get('start') === '1') start(false);
    document.querySelectorAll('[data-quiz-start]').forEach(function (btn) {
      btn.addEventListener('click', function (ev) { ev.preventDefault(); start(true); });
    });
  };

  /* room quantity model: roomCounts is the source of truth; projectRooms mirrors its keys */
  SibQuiz.prototype.setRoomCount = function (key, n) {
    n = Math.max(0, Math.min(9, n));
    if (n === 0) {
      delete this.answers.roomCounts[key];
      this.answers.projectRooms = this.answers.projectRooms.filter(function (k) { return k !== key; });
    } else {
      this.answers.roomCounts[key] = n;
      if (this.answers.projectRooms.indexOf(key) < 0) this.answers.projectRooms.push(key);
    }
    this.persist();
  };

  /* Never write back after a successful submit. finish() deliberately clears the store — but it then
     calls showReport() -> refresh() -> persist(), which used to rewrite it immediately, answers and
     ALREADY-SPENT submissionId and all. A reload of the report (or Back/Forward, or iOS Safari
     re-fetching a reclaimed tab) restored that id, reset `submitted`, and auto-started the quiz
     pre-filled via ?start=1. The next submit was then answered {ok:true,duplicate:true} by the
     worker's idempotency check, and the visitor saw "A copy is on its way to your inbox" for a lead
     that was never saved and never emailed. No outage or network failure is required to trigger it —
     just a reload. It is also exactly the "customer redoes the quiz in the same session" path, which
     is common. Guarded inside persist() rather than at the call site because refresh() is only one of
     its nine callers. See LEAD-PIPELINE-AUDIT.md P0-2. */
  SibQuiz.prototype.persist = function () {
    if (this.submitted) return;
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify({ answers: this.answers, submissionId: this.submissionId, savedAt: Date.now() })); } catch (e) {}
  };

  /* Anonymous drop-off beacon (2026-08-29) — fire-and-forget, no PII: just this.submissionId
     (a random UUID, meaningless on its own unless this session later actually submits) plus
     which step was reached. Lets the studio see where real visitors abandon the quiz, and gives
     a live trace for a session even if the final submit itself later fails for any reason. Never
     touches q.pii, never blocks rendering, and a failed beacon is silently swallowed — this must
     never be able to break the quiz for a visitor. */
  SibQuiz.prototype.trackStep = function (stepKey, stepIndex) {
    var endpoint = /^https?:\/\//i.test(this.cfg.endpoint || '') ? this.cfg.endpoint : '';
    if (!endpoint) return;
    var key = stepKey || this.screens[this.idx];
    var idx = (typeof stepIndex === 'number') ? stepIndex : this.idx;
    try {
      fetch(endpoint + '/progress', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissionId: this.submissionId, stepIndex: idx, stepKey: key }),
        keepalive: true
      }).catch(function () {});
    } catch (e) {}
  };

  /* copy helper: section setting, else code fallback */
  SibQuiz.prototype.t = function (key, fallback) {
    var c = this.cfg.copy || {};
    return c[key] != null && c[key] !== '' ? c[key] : fallback;
  };

  /* 'works' only appears once the visitor asks for a contractor estimate. Site condition is
     just New developer unit vs Resale/older — no age follow-up; resale properties use the
     model's conservative "Unknown old" age band throughout (matches 06_Age_Condition).
     'purpose' (own stay vs rental) sits immediately after 'site', per spec. */
  SibQuiz.prototype.buildScreenList = function () {
    var list = ['site', 'purpose', 'home', 'size', 'rooms', 'property', 'location', 'services', 'budget'];
    if (this.contractorApplies()) list.push('contractor');
    if (this.answers && this.worksIncluded()) list.push('works');
    return list.concat(['name', 'contact']);
  };

  /* id-anchored navigation: the list can grow/shrink at the contractor branch */
  SibQuiz.prototype.go = function (delta) {
    var cur = this.screens[this.idx];
    this.screens = this.buildScreenList();
    var i = this.screens.indexOf(cur);
    if (i < 0) i = Math.min(this.idx, this.screens.length - 1);
    this.idx = Math.max(0, Math.min(this.screens.length - 1, i + delta));
    this.render();
    this.trackStep();
    window.scrollTo({ top: 0, behavior: 'auto' });
  };

  SibQuiz.prototype.setProgress = function (pct) {
    if (pct == null) pct = Math.round(((this.idx + 1) / this.screens.length) * 100);
    if (this.progressBar) {
      this.progressBar.style.width = pct + '%';
      this.progressBar.parentElement.setAttribute('aria-valuenow', String(pct));
    }
  };

  SibQuiz.prototype.announce = function (msg) { if (this.liveRegion) this.liveRegion.textContent = msg; };

  /* option list: use the section's list only when its keys match the code contract */
  SibQuiz.prototype.opts = function (name) {
    var cfgList = this.cfg[name];
    var keys = KEYS[name];
    if (Array.isArray(cfgList) && cfgList.length === keys.length &&
        cfgList.every(function (o) { return o && keys.indexOf(o.value) >= 0; })) return cfgList;
    return OPTS[name];
  };

  /* ---------- rendering ---------- */
  SibQuiz.prototype.render = function () {
    var q = this, id = q.screens[q.idx], E = q.E;
    q.setProgress();
    q.root.innerHTML = '';
    var s = el('div', 'sq-screen');
    var validState = { valid: false };
    var head = el('div', 'sq-head');

    var screens = {
      site: function () {
        head.appendChild(el('h2', 'sq-h', q.t('site_heading', 'Is this a new developer unit, or a resale / older property?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('siteConditions'), 'existingSiteCondition', validState, true));
        validState.valid = validators.oneOf(q.answers.existingSiteCondition, KEYS.siteConditions);
        validState.noNext = true;
      },
      purpose: function () {
        head.appendChild(el('h2', 'sq-h', q.t('purpose_heading', 'Is this property for your own stay, or for rental?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('projectPurposes'), 'projectPurpose', validState, true, function (opt) {
          /* Rental pre-selects Interior Styling + Procurement Service (assuming minimal works)
             — only if services haven't been touched yet, so it never clobbers a manual pick
             made earlier in the flow (e.g. after Back-ing up from a later screen). Selections
             stay fully editable on the Services screen either way. */
          if (opt.value === 'rental' && q.answers.services.length === 0) {
            q.answers.services = RENTAL_DEFAULT_SERVICES.slice();
            q.persist();
          }
        }));
        validState.valid = validators.oneOf(q.answers.projectPurpose, KEYS.projectPurposes);
        validState.noNext = true;
      },
      home: function () {
        head.appendChild(el('h2', 'sq-h', q.t('home_heading', 'What type of home is this?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('homeTypes'), 'homeType', validState, true));
        validState.valid = validators.oneOf(q.answers.homeType, KEYS.homeTypes);
        validState.noNext = true;
      },
      size: function () {
        head.appendChild(el('h2', 'sq-h', q.t('size_heading', 'Approximately how large is your home?')));
        s.appendChild(head);
        var wrap = el('div', 'sq-sliderwrap');
        var row = el('div', 'sq-sliderrow');
        row.appendChild(el('span', 'sq-sllabel', q.t('size_label', 'Built-up area')));
        var out = el('span', 'sq-slout', q.answers.sqft.toLocaleString('en-MY') + ' sq ft');
        row.appendChild(out);
        wrap.appendChild(row);
        var slider = el('input');
        slider.type = 'range';
        slider.min = String(E.LIMITS.minSqft); slider.max = String(E.LIMITS.maxSqft);
        slider.step = '50'; slider.value = String(q.answers.sqft);
        slider.setAttribute('aria-label', 'Built-up area in square feet');
        slider.addEventListener('input', function () {
          q.answers.sqft = E.clampSqft(slider.value);
          out.textContent = q.answers.sqft.toLocaleString('en-MY') + ' sq ft';
          q.persist();
        });
        wrap.appendChild(slider);
        s.appendChild(wrap);
        validState.valid = true;
        validState.alwaysNext = true;
      },
      rooms: function () {
        head.appendChild(el('h2', 'sq-h', q.t('rooms_heading', 'Which spaces would you like help with?')));
        head.appendChild(el('p', 'sq-sub', q.t('rooms_sub', 'Select as many as you like.')));
        s.appendChild(head);
        var roomOpts = (q.cfg.rooms || []).filter(function (r) { return E.ROOMS[r.key]; });
        if (!roomOpts.length) roomOpts = Object.keys(E.ROOMS).map(function (k) { return { key: k, label: E.ROOMS[k] }; });
        if (!roomOpts.some(function (r) { return r.key === 'oth'; })) roomOpts.push({ key: 'oth', label: 'Others' });
        var grid = el('div', 'sq-roomgrid');
        grid.setAttribute('role', 'group'); grid.setAttribute('aria-label', q.t('rooms_heading', 'Which spaces would you like help with?'));
        roomOpts.forEach(function (room) {
          var n = q.answers.roomCounts[room.key] || 0;
          var item = el('div', 'sq-roomitem');
          var b = el('button', 'sq-roomcircle');
          b.type = 'button'; b.setAttribute('aria-pressed', n > 0 ? 'true' : 'false');
          b.setAttribute('aria-label', room.label + (n > 0 ? ', selected × ' + n : ''));
          var shortLabel = room.label.replace(/\s*\(.*\)\s*/, '');
          b.innerHTML = (ICONS[room.key] || '') +
            '<span class="sq-roomlabelin">' + shortLabel + '</span>' +
            (n > 0 ? '<span class="sq-roomcheck" aria-hidden="true">✓</span>' : '');
          b.addEventListener('click', function () { q.setRoomCount(room.key, n > 0 ? 0 : 1); q.render(); });
          item.appendChild(b);
          if (room.key === 'opn') {
            var info = el('details', 'sq-roominfo');
            info.appendChild(el('summary', null, 'What is this?'));
            var tip = el('div', 'sq-roomtip', 'Living, dining and the (dry) kitchen sharing one continuous space — the layout most Klang Valley condos come with.');
            tip.setAttribute('role', 'note');
            info.appendChild(tip);
            item.appendChild(info);
          }
          if (n > 0) {
            var step = el('div', 'sq-roomstep');
            var minus = el('button', null, '&minus;'); minus.type = 'button';
            minus.setAttribute('aria-label', 'Fewer: ' + room.label);
            minus.addEventListener('click', function () { q.setRoomCount(room.key, n - 1); q.render(); });
            var plus = el('button', null, '+'); plus.type = 'button';
            plus.setAttribute('aria-label', 'More: ' + room.label);
            plus.addEventListener('click', function () { q.setRoomCount(room.key, n + 1); q.render(); });
            step.appendChild(minus);
            step.appendChild(el('span', 'sq-roomcount', String(n)));
            step.appendChild(plus);
            item.appendChild(step);
          }
          grid.appendChild(item);
        });
        s.appendChild(grid);
        validState.valid = validators.nonEmptyArray(q.answers.projectRooms);
        validState.alwaysNext = true;
        var total = q.answers.projectRooms.reduce(function (sum, k) { return sum + (q.answers.roomCounts[k] || 1); }, 0);
        validState.totalLabel = total + (total === 1 ? ' room' : ' rooms');
      },
      property: function () {
        head.appendChild(el('h2', 'sq-h', q.t('q5_heading', 'What is the name of the property?')));
        s.appendChild(head);
        s.appendChild(q.textField('propertyName', q.t('q5_placeholder', 'e.g. The Ridge, Ara Damansara'), 'text', validState, false));
        validState.valid = true;
      },
      location: function () {
        head.appendChild(el('h2', 'sq-h', q.t('q6_heading', 'Where is your home located?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('locations'), 'location', validState, true));
        var note = q.t('q6_note', 'Our interior design and interior styling services are currently offered for properties in Klang Valley.');
        if (note) s.appendChild(el('p', 'sq-note', note));
        validState.valid = validators.oneOf(q.answers.location, KEYS.locations);
        validState.noNext = true;
      },
      services: function () {
        head.appendChild(el('h2', 'sq-h', q.t('services_heading', 'How would you like us to help?')));
        head.appendChild(el('p', 'sq-sub', q.t('services_sub', 'Select all that apply.')));
        s.appendChild(head);
        var grid = el('div', 'sq-grid');
        grid.setAttribute('role', 'group'); grid.setAttribute('aria-label', q.t('services_heading', 'How would you like us to help?'));
        q.opts('services').forEach(function (opt) {
          var on = q.answers.services.indexOf(opt.value) >= 0;
          var b = el('button', 'sq-card');
          b.type = 'button'; b.setAttribute('aria-pressed', on ? 'true' : 'false');
          b.innerHTML = '<span class="sq-cardlabel">' + opt.label + '</span>' + (opt.desc ? '<span class="sq-carddesc">' + opt.desc + '</span>' : '');
          b.addEventListener('click', function () {
            var svcs = q.answers.services;
            var i = svcs.indexOf(opt.value);
            if (i >= 0) { svcs.splice(i, 1); }
            else if (opt.value === EXCLUSIVE_SERVICE) { q.answers.services = [EXCLUSIVE_SERVICE]; }
            else {
              var j = svcs.indexOf(EXCLUSIVE_SERVICE);
              if (j >= 0) svcs.splice(j, 1);
              svcs.push(opt.value);
            }
            q.persist(); q.render();
          });
          grid.appendChild(b);
        });
        s.appendChild(grid);
        if (q.answers.projectPurpose === 'rental') {
          var rentalNote = q.t('purpose_rental_note', 'For rental units, we’ve preselected Interior Styling and Procurement Service as we’re assuming minimal works are required. If you’d like a full interior design, you can select Interior Design instead of Interior Styling.');
          if (rentalNote) s.appendChild(el('p', 'sq-note', rentalNote));
        }
        validState.valid = validators.nonEmptyArray(q.answers.services);
        validState.alwaysNext = true;
      },
      budget: function () {
        head.appendChild(el('h2', 'sq-h', q.t('budget_heading', 'How much are you planning to invest in your home?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('budgetBands'), 'budgetBand', validState, true));
        validState.valid = validators.oneOf(q.answers.budgetBand, KEYS.budgetBands);
        validState.noNext = true;
      },
      contractor: function () {
        head.appendChild(el('h2', 'sq-h', q.t('contractor_heading', 'Would you like us to include an estimate for contractor works?')));
        s.appendChild(head);
        s.appendChild(q.choiceGrid(q.opts('contractorPrefs'), 'contractorPref', validState, true));
        validState.valid = validators.oneOf(q.answers.contractorPref, KEYS.contractorPrefs);
        validState.noNext = true;
      },
      works: function () {
        head.appendChild(el('h2', 'sq-h', q.t('works_heading', 'Contractor works')));
        head.appendChild(el('p', 'sq-sub sq-worksintro', q.t('works_intro', 'We design and project-manage — the building work is carried out by trusted KL contractors. We gather and compare their quotes, and help you spend your budget where it matters most. Use this range to plan.')));
        s.appendChild(head);
        s.appendChild(el('p', 'sq-lbl', q.t('works_finish_label', 'Finish level — this is where materials and brands live')));
        var tg = el('div', 'sq-tiergrid');
        q.opts('tiers').forEach(function (t) {
          var b = el('button', 'sq-tier' + (q.answers.tier === t.value ? ' on' : ''));
          b.type = 'button'; b.setAttribute('aria-pressed', q.answers.tier === t.value ? 'true' : 'false');
          b.innerHTML = '<b>' + t.label + '</b>' + tierDescHtml(t.desc);
          b.addEventListener('click', function () { q.answers.tier = t.value; q.persist(); q.render(); });
          tg.appendChild(b);
        });
        s.appendChild(tg);
        s.appendChild(el('p', 'sq-lbl', q.t('works_include_label', 'What the works include')));
        var wg = el('div', 'sq-scpgrid');
        q.opts('works').forEach(function (w) {
          var lab = el('label', 'sq-scp');
          var cb = el('input'); cb.type = 'checkbox';
          cb.checked = q.answers.works.indexOf(w.value) >= 0;
          cb.addEventListener('change', function () {
            var i = q.answers.works.indexOf(w.value);
            if (cb.checked && i < 0) q.answers.works.push(w.value);
            if (!cb.checked && i >= 0) q.answers.works.splice(i, 1);
            q.persist();
          });
          lab.appendChild(cb);
          lab.appendChild(el('span', null, w.label));
          wg.appendChild(lab);
        });
        s.appendChild(wg);
        validState.valid = true;
        validState.alwaysNext = true;
      },
      name: function () {
        head.appendChild(el('h2', 'sq-h', q.t('q8_heading', 'What should we call you?')));
        s.appendChild(head);
        s.appendChild(q.textField('__name', q.t('q8_placeholder', 'Your name'), 'text', validState, true));
        validState.valid = validators.required(q.pii.name);
      },
      contact: function () {
        var first = (q.pii.name.trim().split(/\s+/)[0]) || 'there';
        /* [property] falls back to a generic word rather than printing an empty gap, since the
           property name is optional. Both placeholders are documented on the Customize field. */
        var prop = (q.answers.propertyName || '').trim()
          || q.t('welcome_home_fallback', 'your home');
        /* esc() both before substitution: el() assigns via innerHTML, so an un-escaped name or
           property name is inserted as MARKUP. propertyName persists in sessionStorage, which made
           this stored rather than merely reflected — it re-ran on every reopen of the quiz in that
           browser. A visitor can only do this to themselves, which is why it was not urgent, but a
           property name containing a '<' also visibly broke the heading for an innocent visitor. */
        var msg = q.t('contact_heading', 'Hi [name], let’s make [property] feel like you.')
          .replace('[name]', esc(first)).replace('{{ name }}', esc(first))
          .replace('[property]', esc(prop)).replace('{{ property }}', esc(prop));
        head.appendChild(el('h2', 'sq-h', msg));
        head.appendChild(el('p', 'sq-sub', q.t('contact_sub1', 'Your personalised estimate is ready.')));
        head.appendChild(el('p', 'sq-sub', q.t('contact_sub2', 'Enter your email to see it now and receive a copy.')));
        s.appendChild(head);
        s.appendChild(q.textField('__email', q.t('q9_placeholder', 'you@email.com'), 'email', validState, true));
        s.appendChild(q.textField('__phone', q.t('q9_phone_placeholder', 'WhatsApp number (e.g. +60 12 345 6789)'), 'tel', validState, true));
        var consentWrap = el('label', 'sq-consent');
        var cb = el('input'); cb.type = 'checkbox'; cb.checked = q.pii.marketingOptIn;
        cb.addEventListener('change', function () { q.pii.marketingOptIn = cb.checked; });
        consentWrap.appendChild(cb);
        consentWrap.appendChild(el('span', null, q.t('q9_consent', 'Also send me occasional home inspiration and studio news.')));
        s.appendChild(consentWrap);
        s.appendChild(el('p', 'sq-privacy', q.t('q9_privacy', 'We use your details only to prepare and send your estimate. No spam, ever.')));
        s.appendChild(el('div', 'sq-status', ''));
        validState.valid = validators.email(q.pii.email) && validators.phone(q.pii.phone);
        validState.nextLabel = q.t('contact_submit', 'See my estimate →');
        validState.isSubmit = true;
        validState.noBack = true;   /* per spec: no Back on the contact screen */
        validState.center = true;   /* CTA centred */
      }
    };
    screens[id]();
    if (id === 'rooms') s.className = 'sq-screen sq-split';

    /* nav: Back left; right cluster = [total] [Next] — or a single centred CTA */
    var nav = el('div', 'sq-nav' + (validState.center ? ' sq-navcenter' : ''));
    if (q.idx > 0 && !validState.noBack) {
      var back = el('button', 'sq-back', q.t('back_label', 'Back'));
      back.type = 'button';
      back.addEventListener('click', function () { q.go(-1); });
      nav.appendChild(back);
    }
    var right = [];
    if (validState.totalLabel) right.push(el('span', 'sq-total', validState.totalLabel));
    if (!validState.noNext && (validState.valid || validState.alwaysNext)) {
      var next = el('button', 'sq-next', validState.nextLabel || q.t('next_label', 'Next'));
      next.type = 'button';
      if (!validState.valid) next.disabled = true;
      else if (validState.isSubmit) next.addEventListener('click', function () { q.submit(next); });
      else next.addEventListener('click', function () { q.go(1); });
      right.push(next);
    }
    right.forEach(function (elm, i) {
      if (!validState.center) elm.style.marginLeft = i === 0 ? 'auto' : '18px';
      nav.appendChild(elm);
    });
    s.appendChild(nav);
    q.root.appendChild(s);
  };

  SibQuiz.prototype.choiceGrid = function (options, field, validState, single, onSelect) {
    var q = this;
    var grid = el('div', 'sq-grid');
    grid.setAttribute('role', 'radiogroup');
    options.forEach(function (opt) {
      var on = q.answers[field] === opt.value;
      var b = el('button', 'sq-card');
      b.type = 'button'; b.setAttribute('role', 'radio'); b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.innerHTML = '<span class="sq-cardlabel">' + opt.label + '</span>' + (opt.desc ? '<span class="sq-carddesc">' + opt.desc + '</span>' : '');
      b.addEventListener('click', function () {
        if (q._advancing) return;
        q.answers[field] = opt.value;
        if (onSelect) onSelect(opt);
        q.persist();
        if (single) {
          q._advancing = true;
          Array.prototype.forEach.call(grid.children, function (x) { x.setAttribute('aria-checked', 'false'); });
          b.setAttribute('aria-checked', 'true');
          setTimeout(function () { q._advancing = false; q.go(1); }, 180);
        } else {
          q.render();
        }
      });
      grid.appendChild(b);
    });
    return grid;
  };

  SibQuiz.prototype.textField = function (field, placeholder, type, validState, required) {
    var q = this;
    var isPII = field === '__name' || field === '__email' || field === '__phone';
    var wrap = el('div', 'sq-fieldwrap');
    var input = el('input', 'sq-field');
    input.type = type; input.placeholder = placeholder || '';
    input.required = !!required;
    input.value = isPII ? (field === '__name' ? q.pii.name : (field === '__phone' ? q.pii.phone : q.pii.email)) : (q.answers[field] || '');
    input.setAttribute('aria-label', placeholder || field);
    var isFinal = field === '__email' || field === '__phone'; /* both live on the contact screen */
    input.addEventListener('input', function () {
      if (field === '__name') q.pii.name = input.value;
      else if (field === '__email') q.pii.email = input.value;
      else if (field === '__phone') q.pii.phone = input.value;
      else { q.answers[field] = input.value; q.persist(); }
      var ok = isFinal ? (validators.email(q.pii.email) && validators.phone(q.pii.phone))
        : (!required || validators.required(input.value));
      var nav = q.root.querySelector('.sq-nav');
      var existing = nav.querySelector('.sq-next');
      if (ok && !existing) {
        var next = el('button', 'sq-next', (isFinal ? q.t('contact_submit', 'See my estimate →') : q.t('next_label', 'Next')));
        next.type = 'button';
        if (isFinal) next.addEventListener('click', function () { q.submit(next); });
        else next.addEventListener('click', function () { q.go(1); });
        if (!nav.classList.contains('sq-navcenter')) next.style.marginLeft = 'auto';
        nav.appendChild(next);
      } else if (!ok && existing) existing.remove();
    });
    input.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter') { ev.preventDefault(); var n = q.root.querySelector('.sq-next'); if (n) n.click(); }
    });
    wrap.appendChild(input);
    if (field !== '__phone') setTimeout(function () { input.focus(); }, 60);
    return wrap;
  };

  /* ---------- estimate state + submit + report ---------- */
  /* engine service key: interior design / virtual design price at the design rate; styling-only at the styling rate */
  SibQuiz.prototype.svcKey = function () {
    var s = this.answers.services;
    if (s.indexOf('design') >= 0 || s.indexOf('virtual') >= 0) return 'd';
    if (s.indexOf('styling') >= 0) return 's';
    return 'd';
  };
  SibQuiz.prototype.contractorApplies = function () {
    return contractorAppliesTo(this.answers && this.answers.services);
  };
  /* Both gates below also check contractorApplies(), not just the stored answer: a visitor can
     answer "Yes, include an estimate", go back, and switch to styling-only. The stale 'include'
     would otherwise still add the works step and print renovation figures on the report. */
  SibQuiz.prototype.worksIncluded = function () {
    return this.contractorApplies() && this.answers.contractorPref === 'include';
  };
  /* Whether the REPORT shows the finish level, works and a renovation figure. Deliberately
     wider than worksIncluded(): 'unsure' never asked for an estimate but never declined one
     either, and showing them the finish level and works — seeded from the defaults, since they
     never saw that step — turns the report into something they can explore rather than a dead
     end. 'own' and 'none' actively declined, so they stay excluded. worksIncluded() still
     governs the QUESTIONNAIRE, so 'unsure' is not dragged through a step they opted out of. */
  SibQuiz.prototype.worksShown = function () {
    if (!this.contractorApplies()) return false;
    var pref = this.answers.contractorPref;
    return pref === 'include' || pref === 'unsure';
  };

  var WORKS_TO_BOQ_FLAGS = function (works) {
    var has = function (k) { return works.indexOf(k) >= 0; };
    return {
      plumbing: has('plumb'), flooring: has('floor'), paintingCeiling: has('paint'),
      electrical: has('elec'), kitchenCarpentry: has('kit'), builtInWardrobe: has('ward'), ffe: has('ffe'),
      wetTiling: has('wet')
    };
  };

  /* Real construction-cost estimate from the Klang Valley BOQ model (sib-boq-engine.js) —
     this is the renovation-cost range; the Sundays in Bed design fee is priced separately
     by sib-estimator.js and is not part of this workbook.
     tierOverride ('e'|'p'|'s') prices the SAME answers at a different finish level without
     mutating state — used to quote a real alternative-tier figure in the budget-fit note,
     so that number comes from the engine rather than being approximated. */
  SibQuiz.prototype.constructionEstimate = function (tierOverride) {
    var q = this, a = q.answers, boq = window.SIBBoqEngine;
    if (!boq) return null;
    var roomCounts = {};
    Object.keys(a.roomCounts).forEach(function (k) {
      var boqKey = k === 'oth' ? 'others' : k;
      roomCounts[boqKey] = a.roomCounts[k];
    });
    return boq.estimateProject({
      existingSiteCondition: a.existingSiteCondition || 'new_developer',
      propertyAgeYears: 0, /* resale with no age given -> conservative "Unknown old" band (06_Age_Condition) */
      propertyPhysicalCondition: 'Average', /* not asked — kept light, per spec's documented default */
      propertyType: HOME_TYPE_TO_PROPERTY_TYPE[a.homeType] || 'Condominium',
      finishTier: TIER_ENGINE_NAME[tierOverride || a.tier] || 'Premium',
      roomCounts: roomCounts,
      works: WORKS_TO_BOQ_FLAGS(a.works),
      layoutChange: 'Keep layout',
      projectPurpose: a.projectPurpose || 'own_stay',
      sqft: a.sqft /* v9: drives the whole-home size-zone engine (MODEL_CONFIG 3D) — without
                      this every submission would silently price as the smallest zone */
    });
  };

  /* Compare the "Plan around" total (design fee + renovation cost — what they actually pay)
     against the budget band they picked, and, when it doesn't fit, price a real alternative
     finish level to point at.

     A tier FITS when its range OVERLAPS the band — not when its top happens to sit under the
     ceiling. That distinction is the whole rule. Curated at RM118,400–228,000 against a
     RM150,000–200,000 budget straddles that budget almost perfectly, but a top-of-range test
     calls it "over" and downsells to Essential at RM65,200–106,400 — a tier sitting ENTIRELY
     below what they told us they were willing to spend. Recommending that under-serves the
     customer and undersells the studio.
       in    = the range overlaps the band, i.e. this tier is the right neighbourhood
       over  = the whole range sits above the ceiling  -> offer the richest tier that overlaps
       under = the whole range sits below the floor    -> offer the richest they can afford
     Note the two directions use different targets on purpose: a downsell must land back inside
     their budget, whereas an upsell should show the best available even if it still comes in
     under (with a big budget, the top tier is simply the most there is).
     Returns null whenever there's nothing useful to say (no works, unknown band, no engine). */
  SibQuiz.prototype.budgetFit = function (fee) {
    var q = this, a = q.answers, E = q.E, self = this;
    if (!q.worksShown()) return null;
    var band = BUDGET_RANGE[a.budgetBand];
    if (!band) return null;
    var bandLow = band[0], bandHigh = band[1];
    /* Plan-around range for a given tier, priced by the real engine (never approximated). */
    function planFor(tier) {
      var b = self.constructionEstimate(tier);
      if (!b || !(b.directBoq.expected > 0)) return null;
      return { lo: fee + b.total.low, hi: fee + b.total.high };
    }
    /* overlaps: this tier lives in the same price neighbourhood as their budget */
    function overlaps(p) { return p && (bandHigh == null || p.lo <= bandHigh) && p.hi >= bandLow; }
    /* affordable: worst case still within their ceiling — the test for an upsell target */
    function affordable(p) { return p && (bandHigh == null || p.hi <= bandHigh); }
    var cur = planFor(a.tier);
    if (!cur) return null;
    var phrase = budgetPhrase(a.budgetBand, optLabel(q.opts('budgetBands'), a.budgetBand));
    var fmtRange = function (x) { return E.fmtRM(E.r100(x.lo)) + ' – ' + E.fmtRM(E.r100(x.hi)); };
    var idx = TIER_ORDER.indexOf(a.tier);

    if (overlaps(cur)) {
      return { state: 'in', segs: [
        { t: ' And that lines up with ' + phrase + ' — feel free to adjust the finish level and works above.' }
      ] };
    }

    if (bandHigh != null && cur.lo > bandHigh) {
      /* Entirely above the ceiling — walk DOWN for the richest tier that still overlaps their
         budget, so the alternative lands in their neighbourhood rather than far beneath it. */
      for (var i = idx - 1; i >= 0; i--) {
        var alt = planFor(TIER_ORDER[i]);
        if (overlaps(alt)) {
          return { state: 'over', segs: [
            { t: ' It sits a little above ' + phrase + ', though — ' },
            { t: TIER_LABEL[TIER_ORDER[i]], b: true },
            { t: ' lands around ' + fmtRange(alt) + ' if you’d like to compare. Feel free to adjust the finish level and works above.' }
          ] };
        }
      }
      return { state: 'over-all', segs: [
        { t: ' It sits above ' + phrase + ' even at ' + TIER_LABEL[TIER_ORDER[0]] + ', our most pared-back finish, so unticking a few works you could defer would bring it closer.' }
      ] };
    }

    {
      /* Entirely below the floor. Prefer the richest tier that OVERLAPS their budget — that is
         the one that actually uses what they said they'd spend. Only if nothing overlaps (a
         budget larger than anything we offer) fall back to the richest they can afford, which
         will still come in under; with a big budget the top tier is simply the most there is. */
      var upTier = null;
      for (var j = TIER_ORDER.length - 1; j > idx; j--) {
        if (overlaps(planFor(TIER_ORDER[j]))) { upTier = TIER_ORDER[j]; break; }
      }
      if (upTier == null) {
        for (var k = TIER_ORDER.length - 1; k > idx; k--) {
          if (affordable(planFor(TIER_ORDER[k]))) { upTier = TIER_ORDER[k]; break; }
        }
      }
      if (upTier != null) {
        var up = planFor(upTier);
        {
          return { state: 'under', segs: [
            { t: ' And oh — looks like you’ve got room. ' },
            { t: TIER_LABEL[upTier], b: true },
            { t: ' lands around ' + fmtRange(up) + ', if you’d like a more detailed finish — feel free to adjust the finish level and works above.' }
          ] };
        }
      }
      return { state: 'under-none', segs: [
        { t: ' That comes in under ' + phrase + ', so there’s room to explore — feel free to adjust the finish level and works above.' }
      ] };
    }

  };

  SibQuiz.prototype.estimateState = function () {
    var q = this, E = q.E;
    var counts = {};
    Object.keys(E.ROOMS).forEach(function (k) { counts[k] = 0; });
    q.answers.projectRooms.forEach(function (k) { if (counts[k] != null) counts[k] = q.answers.roomCounts[k] || 1; });
    return {
      counts: counts,
      sqft: q.answers.sqft,
      svc: q.svcKey(),
      tier: q.worksShown() ? q.answers.tier : q.E.DEFAULTS.tier,
      works: q.worksShown() ? q.answers.works.slice() : []
    };
  };
  SibQuiz.prototype.calculatorURL = function () {
    var st = this.estimateState();
    var rooms = Object.keys(st.counts).filter(function (k) { return st.counts[k] > 0; })
      .map(function (k) { return k + ':' + st.counts[k]; }).join(',');
    return (this.cfg.calculatorPath || '/pages/cost-calculator') + '?quiz=1&rooms=' + encodeURIComponent(rooms) +
      '&sqft=' + st.sqft + '&svc=' + st.svc + '&tier=' + st.tier + '&works=' + encodeURIComponent(st.works.join(','));
  };

  /* Plain-text lead summary for the studio (Shopify contact-form fallback). */
  SibQuiz.prototype.leadSummary = function () {
    var q = this, E = q.E, a = q.answers;
    var st = q.estimateState();
    var r = E.estimate(st);
    var boq = q.worksShown() ? q.constructionEstimate() : null;
    var included = !!(boq && boq.directBoq.expected > 0);
    var rooms = Object.keys(st.counts).filter(function (k) { return st.counts[k] > 0; })
      .map(function (k) { return E.ROOMS[k] + (st.counts[k] > 1 ? ' x' + st.counts[k] : ''); }).join(', ');
    var services = a.services.map(function (v) { return optLabel(q.opts('services'), v); }).join(' + ');
    var lines = [
      'New estimate enquiry from ' + q.pii.name.trim(),
      '',
      'Email: ' + q.pii.email.trim(),
      'WhatsApp: ' + q.pii.phone.trim(),
      'Newsletter opt-in: ' + (q.pii.marketingOptIn ? 'yes' : 'no'),
      '',
      'Property: ' + (a.propertyName || '—'),
      'Site condition: ' + optLabel(q.opts('siteConditions'), a.existingSiteCondition),
      'Own stay or rental: ' + optLabel(q.opts('projectPurposes'), a.projectPurpose),
      'Location: ' + optLabel(q.opts('locations'), a.location),
      'Home type: ' + optLabel(q.opts('homeTypes'), a.homeType),
      'Size: ' + a.sqft.toLocaleString('en-MY') + ' sq ft',
      'Spaces: ' + rooms,
      'Services: ' + (services || '—'),
      'Planned investment: ' + optLabel(q.opts('budgetBands'), a.budgetBand)
    ];
    /* Only report the renovation answer when the question was actually asked — otherwise this
       reads "Renovation cost: —" for a styling client who was never offered the choice. */
    if (q.contractorApplies()) lines.push('Renovation cost: ' + optLabel(q.opts('contractorPrefs'), a.contractorPref));
    lines.push('', 'Design fee (fixed): ' + E.fmtRM(r.fee));
    if (included) {
      var tierName = TIER_LABEL[a.tier] || 'Curated';
      lines.push('Finish level: ' + tierName);
      lines.push('Works: ' + a.works.map(function (w) { return optLabel(OPTS.works, w); }).join(', '));
      lines.push('Renovation cost, BOQ model (estimated): ' + E.fmtRM(E.r100(boq.total.low)) + ' - ' + E.fmtRM(E.r100(boq.total.high)));
      lines.push('Age band used: ' + boq.ageBand + ' | recommended contingency (reference only, not in total): ' + Math.round(boq.recommendedContingency * 1000) / 10 + '%');
      lines.push('Plan around: ' + E.fmtRM(E.r100(r.fee + boq.total.low)) + ' - ' + E.fmtRM(E.r100(r.fee + boq.total.high)));
    } else {
      lines.push('Renovation cost: not included in this estimate');
      lines.push('Plan around: ' + E.fmtRM(r.fee));
    }
    lines.push('', 'Adjustable version: ' + location.origin + q.calculatorURL());
    return lines.join('\n');
  };

  /* Fallback delivery: Shopify's native contact form — reaches the studio inbox with no third-party keys. */
  SibQuiz.prototype.sendViaShopifyContact = function () {
    var q = this;
    var body = new URLSearchParams();
    body.set('form_type', 'contact');
    body.set('utf8', '✓');
    body.set('contact[name]', q.pii.name.trim() + ' (estimate flow)');
    body.set('contact[email]', q.pii.email.trim());
    body.set('contact[phone]', q.pii.phone.trim());
    body.set('contact[body]', q.leadSummary());
    return fetch('/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString()
    }).then(function (res) {
      return res.text().catch(function () { return ''; }).then(function (html) {
        /* res.ok alone is NOT a success signal here, and this path now carries every failed
           submission rather than only the rare 4xx. fetch follows Shopify's 302 transparently, so
           res.ok describes the final page render — and a validation rejection, a throttle, and a
           bot challenge are all ALSO 200 HTML pages. Look for Shopify's actual posted marker.
           Unconfirmed-but-2xx is still reported as delivered (that is the long-standing behaviour
           and this is not the change to regress it on), but it warns loudly so a live test shows
           up immediately — whether this path has ever delivered a single email is still an open
           question in LEAD-PIPELINE-AUDIT.md §8. */
        var confirmed = /contact_posted=true/.test(res.url || '') || /contact_posted=true/.test(html);
        if (res.ok && !confirmed && window.console && console.warn) {
          console.warn('sib-quiz: contact-form rescue returned 200 but no contact_posted marker — delivery UNCONFIRMED');
        }
        return { ok: res.ok, confirmed: confirmed };
      });
    });
  };

  /* The post-submission report: same visual structure as /pages/cost-calculator.
     The customer's EMAIL is already sent server-side with the submitted answers (frozen) before
     this renders — so editing here never changes what was emailed. On the website, when a
     contractor estimate was requested, the Finish level and Works controls stay EDITABLE so the
     contractor price can be adjusted live; the figures recompute in place. Everything else
     (property, rooms, service, etc.) is read-only.
     Design fee = sib-estimator.js (per-room service fee); contractor works = the Klang Valley
     BOQ model (sib-boq-engine.js), the sole costing authority for that half. Finish tier drives
     both — as the live calculator already behaves. */
  SibQuiz.prototype.showReport = function (statusMsg) {
    var q = this, E = q.E, a = q.answers;
    var included = q.worksShown();
    /* Lead sentence is emphasised and kept separate so the single most important caveat —
       this is an estimate, not a quote — is read even when the rest is skimmed. */
    var discLead = q.t('report_disclaimer_lead', 'This is an estimate, not a quote.');
    var disc = q.t('report_disclaimer', 'Every figure is confirmed against real contractor quotes before anything is agreed. Structural and wet works sit outside this range. We design and project-manage — trusted KL contractors do the building, and we gather and compare their quotes for you. Klang Valley site visits are RM200, deducted from your project when you go ahead with us.');

    var st = q.estimateState();
    var rooms = Object.keys(st.counts).filter(function (k) { return st.counts[k] > 0; })
      .map(function (k) { return E.ROOMS[k].replace(/\s*\(.*\)\s*/, '') + (st.counts[k] > 1 ? ' ×' + st.counts[k] : ''); }).join(', ');
    var services = a.services.map(function (v) { return optLabel(q.opts('services'), v); }).join(' + ');

    var sumRows = [
      ['Property', a.propertyName.trim() || '—'],
      ['Site condition', optLabel(q.opts('siteConditions'), a.existingSiteCondition)],
      ['Own stay or rental', optLabel(q.opts('projectPurposes'), a.projectPurpose)],
      ['Location', optLabel(q.opts('locations'), a.location)],
      ['Home type', optLabel(q.opts('homeTypes'), a.homeType)],
      ['Built-up area', a.sqft.toLocaleString('en-MY') + ' sq ft'],
      ['Spaces', rooms || '—'],
      ['Service', services || '—'],
      ['Planned investment', optLabel(q.opts('budgetBands'), a.budgetBand)]
    ];
    if (!included && q.contractorApplies()) sumRows.push(['Renovation cost', optLabel(q.opts('contractorPrefs'), a.contractorPref)]);

    var wrapEl = el('div', 'sq-report');
    if (statusMsg) wrapEl.appendChild(el('p', 'sq-repstatus', esc(statusMsg)));

    var sum = el('div', 'sq-repsum');
    sum.appendChild(el('h3', null, esc(q.t('report_heading', 'Here’s your estimate'))));
    sumRows.forEach(function (row) {
      var r = el('div', 'sq-reprow');
      r.appendChild(el('span', 'sq-replbl', esc(row[0])));
      r.appendChild(el('span', 'sq-repval', esc(row[1])));
      sum.appendChild(r);
    });
    wrapEl.appendChild(sum);

    /* editable finish-level + works block (only when a contractor estimate was requested) */
    if (included) {
      var edit = el('div', 'sq-repedit');
      var editHead = el('div', 'sq-edithead');
      editHead.appendChild(el('h3', 'sq-edittitle', q.t('report_edit_title', 'Change the finish level or works')));
      editHead.appendChild(el('p', 'sq-editsub', q.t('report_edit_note', 'The estimate updates as you go. Your emailed copy keeps the answers you submitted.')));
      edit.appendChild(editHead);
      edit.appendChild(el('p', 'sq-lbl', q.t('works_finish_label', 'Finish level — this is where materials and brands live')));
      var tg = el('div', 'sq-tiergrid');
      q.opts('tiers').forEach(function (t) {
        var b = el('button', 'sq-tier' + (a.tier === t.value ? ' on' : ''));
        b.type = 'button'; b.setAttribute('aria-pressed', a.tier === t.value ? 'true' : 'false');
        b.innerHTML = '<b>' + t.label + '</b>' + tierDescHtml(t.desc);
        b.addEventListener('click', function () { a.tier = t.value; refresh(); });
        tg.appendChild(b);
      });
      edit.appendChild(tg);
      edit.appendChild(el('p', 'sq-lbl', q.t('works_include_label', 'What the works include')));
      var wg = el('div', 'sq-scpgrid');
      q.opts('works').forEach(function (w) {
        var lab = el('label', 'sq-scp');
        var cb = el('input'); cb.type = 'checkbox';
        cb.checked = a.works.indexOf(w.value) >= 0;
        cb.addEventListener('change', function () {
          var i = a.works.indexOf(w.value);
          if (cb.checked && i < 0) a.works.push(w.value);
          if (!cb.checked && i >= 0) a.works.splice(i, 1);
          refresh();
        });
        lab.appendChild(cb);
        lab.appendChild(el('span', null, w.label));
        wg.appendChild(lab);
      });
      edit.appendChild(wg);
      wrapEl.appendChild(edit);
    }

    /* answer panel — figures get stable classes so refresh() can update them in place */
    var ans = el('div', 'sq-repans');
    var sep = el('div', 'sq-anssep');
    var feeRow = el('div', 'sq-ansrow');
    feeRow.appendChild(el('span', 'sq-anslbl', 'Design fee — fixed'));
    feeRow.appendChild(el('span', 'sq-ansval sq-rep-fee', ''));
    sep.appendChild(feeRow);
    sep.appendChild(el('div', 'sq-ansplus', '+'));
    var worksRow = el('div', 'sq-ansrow');
    worksRow.appendChild(el('span', 'sq-anslbl', 'Renovation cost — estimated'));
    worksRow.appendChild(el('span', 'sq-ansval sq-rep-works', ''));
    sep.appendChild(worksRow);
    ans.appendChild(sep);
    var planRow = el('div', 'sq-ansrow');
    planRow.appendChild(el('span', 'sq-anslbl', 'Plan around'));
    planRow.appendChild(el('span', 'sq-anstotal sq-rep-plan', ''));
    ans.appendChild(planRow);
    /* Advisor: talks the figures above through, streamed on scroll, CTA easing in at the end.
       This replaces the old static "Most X projects..." note — that sentence is now the
       advisor's opening beat, so the same thing isn't said twice on the page. */
    var adv = el('div', 'sq-adv sq-rep-adv');
    var advHead = el('div', 'sq-advhead');
    var wave = el('div', 'sq-advwave');
    wave.setAttribute('aria-hidden', 'true');
    for (var wi = 0; wi < 5; wi++) wave.appendChild(el('i'));
    advHead.appendChild(wave);
    advHead.appendChild(el('span', 'sq-advname', q.t('advisor_name', 'Your design advisor')));
    adv.appendChild(advHead);
    var advBub = el('div', 'sq-advbub sq-rep-advbub');
    advBub.setAttribute('aria-hidden', 'true'); /* SR reads the complete copy below instead */
    adv.appendChild(advBub);
    var advSr = el('div', 'sq-advsr sq-rep-advsr');
    adv.appendChild(advSr);
    ans.appendChild(adv);
    var ctaWrap = el('div', 'sq-advcta sq-rep-cta'); ctaWrap.style.textAlign = 'center';
    var cta = el('a', 'sq-anscta', esc(q.t('report_cta_label', 'Schedule a discovery call →')));
    cta.href = q.t('report_cta_link', '/pages/contact');
    ctaWrap.appendChild(cta);
    ans.appendChild(ctaWrap);
    var discEl = el('p', 'sq-ansdisc');
    discEl.innerHTML = '<strong>' + esc(discLead) + '</strong> ' + esc(disc);
    ans.appendChild(discEl);
    wrapEl.appendChild(ans);

    /* Advisor playback. It streams ONCE — the first time the panel is scrolled into view.
       After that, changing a finish level or work re-renders the message instantly instead of
       replaying the typing, because re-watching the same three sentences crawl out on every
       checkbox would get old fast (and would fight the customer, who is mid-comparison). */
    var advReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var advState = {
      beats: null, revealed: false, faded: false, ctaGuard: null,
      /* The CTA starts visible; these only ever hide it for the duration of the typing, and
         every exit path (finished, cancelled, threw, took too long) runs showCta. */
      showCta: function () {
        if (advState.ctaGuard) { clearTimeout(advState.ctaGuard); advState.ctaGuard = null; }
        ctaWrap.classList.remove('pending');
      },
      holdCtaDuringFade: function () {
        ctaWrap.classList.add('pending');
        if (advState.ctaGuard) clearTimeout(advState.ctaGuard);
        /* Last-resort unhide: if the fade never completes, the button still comes back. */
        advState.ctaGuard = setTimeout(advState.showCta, 9000);
      },
      play: function () {
        if (!advState.beats) return;
        advBub.innerHTML = beatsToHtml(advState.beats);
        adv.classList.remove('talking');
        if (advState.faded || advReduced) { advState.showCta(); return; }
        advState.faded = true;
        /* Rows fade on staggered CSS delays (see .sq-advbub p). Hold the CTA only for that
           window, then bring it in — bounded and short, so the fail-safe still holds. */
        var rows = advState.beats.length;
        var lastRowDoneMs = 750 + (rows - 1) * 550 + 500; /* keep in step with the nth-child delays above */
        advState.holdCtaDuringFade();
        adv.classList.add('talking');
        setTimeout(function () { adv.classList.remove('talking'); }, lastRowDoneMs);
        setTimeout(advState.showCta, lastRowDoneMs + 150);
      }
    };

    /* recompute figures from current answers and paint them into the panel */
    function refresh() {
      var st2 = q.estimateState();
      var r2 = E.estimate(st2);
      var boq = included ? q.constructionEstimate() : null;
      var hasWorks = !!(boq && boq.directBoq.expected > 0);
      var tierName = TIER_LABEL[a.tier] || 'Curated';
      var worksVal = hasWorks ? (E.fmtRM(E.r100(boq.total.low)) + ' – ' + E.fmtRM(E.r100(boq.total.high))) : q.t('report_works_excluded', 'Not included in this estimate');
      var planVal = hasWorks ? (E.fmtRM(E.r100(r2.fee + boq.total.low)) + ' – ' + E.fmtRM(E.r100(r2.fee + boq.total.high))) : E.fmtRM(r2.fee);
      q.root.querySelector('.sq-rep-fee').textContent = E.fmtRM(r2.fee);
      var wEl = q.root.querySelector('.sq-rep-works');
      wEl.textContent = worksVal; wEl.style.fontSize = hasWorks ? '' : '17px';
      q.root.querySelector('.sq-rep-plan').textContent = planVal;
      /* Advisor script — rebuilt on every toggle so it always describes the live figures.
         Two rows: the read (greeting + band + budget fit, as one flowing thought) and the
         invitation. The budget fit is appended INTO the first row rather than given its own
         paragraph — it's the same sentence continuing, and it keeps the whole thing short. */
      var fit = hasWorks ? q.budgetFit(r2.fee) : null;
      var beats = [];
      var open = [{ t: 'Hi there! ' }];
      if (hasWorks) {
        if (a.contractorPref === 'unsure') {
          /* They told us they weren't sure, so say so rather than presenting a figure as
             though they'd asked for it — then hand them the controls. */
          open.push({ t: 'You weren’t sure about renovation works yet, so here’s a starting point — a ' },
            { t: tierName, b: true },
            { t: ' finish with the most common works included.' });
        } else {
          open.push({ t: 'Most ' }, { t: tierName, b: true },
            { t: ' projects of this size and scope land inside this band.' });
        }
        if (fit && fit.segs) open = open.concat(fit.segs);
        /* What actually moves the number, stated as options rather than advice. Ticking
           furniture &amp; fittings roughly tripled one real enquiry's works figure against an
           otherwise comparable project, with nothing in the quote naming it as the cause.
           Kept non-directive: name the levers, do not tell the reader to pull one.
           Must stay in step with the same sentence in worker.js buildEmail — the reader
           sees this page first and the email second. */
        open.push({ t: ' Scope moves this range as much as finish does — the number of spaces included, and whether furniture and fittings sit inside the budget, are usually the two largest levers. We can price any combination you’d like to see.' });
      } else {
        /* No renovation figure. WHY there isn't one changes what the reader needs to hear, so
           name the actual reason rather than printing one line for every case — a styling
           client reading "you didn't ask for contractor works" would be baffled, since they
           were never asked the question. Narrowest cause first; the final else is a genuine
           catch-all so a future contractor option can't fall through to an empty advisor. */
        if (!q.contractorApplies()) {
          /* Styling / procurement only, so the contractor step never ran. Point at the service
             that would produce a renovation estimate instead of implying they declined one. */
          open.push({ t: q.t('report_note_nodesign', 'This is your fixed design fee. Renovation costs come with Interior Design — the service(s) you’ve chosen don’t involve building works.') });
        } else if (a.contractorPref === 'own') {
          open.push({ t: q.t('report_note_owncontractor', 'This is your fixed design fee. You’re bringing your own contractor, so we’ve left the renovation costs with them.') });
        } else {
          open.push({ t: q.t('report_note_noworks', 'This is your fixed design fee. You told us there are no renovation works, so there’s nothing further to estimate.') });
        }
      }
      beats.push(open);
      beats.push([{ t: q.t('advisor_close', 'Keen to take it further? A short call is all it takes — no obligation, just a clearer picture of your home.') }]);
      advState.beats = beats;
      advSr.textContent = beatsToText(beats);
      if (advState.revealed) advState.play();
      /* keep tier-card selected state in sync when re-clicked */
      Array.prototype.forEach.call(q.root.querySelectorAll('.sq-tier'), function (b) {
        var on = b.querySelector('b') && q.opts('tiers').filter(function (t) { return t.label === b.querySelector('b').textContent; })[0];
        var isOn = on && on.value === a.tier;
        b.classList.toggle('on', !!isOn); b.setAttribute('aria-pressed', isOn ? 'true' : 'false');
      });
      q.persist();
    }

    q.setProgress(100);
    q.root.innerHTML = '';
    q.root.appendChild(wrapEl);
    refresh();
    /* Start the advisor only once the money panel is actually on screen — it's talking about
       figures the customer is looking at, so it must not have already played to an empty room
       by the time they scroll down. Without IntersectionObserver, just show it. */
    (function () {
      var fallback = null;
      function begin(instant) {
        if (advState.revealed) return;
        advState.revealed = true;
        if (fallback) { clearTimeout(fallback); fallback = null; }
        adv.classList.add('in');
        if (instant) advState.faded = true; /* skip the fade — nobody is watching by now */
        advState.play();
      }
      if (!('IntersectionObserver' in window)) { begin(false); return; }
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { io.unobserve(entry.target); begin(false); }
        });
      }, { threshold: 0.35 });
      io.observe(adv);
      /* Safety net: the CTA only appears once the advisor finishes speaking, so if the observer
         never fires — a browser quirk, an embedding that breaks intersection, a customer who
         never scrolls this far — the button would stay invisible and the conversion is lost.
         After a generous delay, reveal the message outright. Getting the CTA on screen matters
         more than the animation. */
      fallback = setTimeout(function () { begin(true); }, 12000);
    })();
    q.announce(q.t('report_heading', 'Here’s your estimate'));
    window.scrollTo({ top: 0 });
  };

  SibQuiz.prototype.submit = function (btn) {
    var q = this;
    if (q.submitting || q.submitted) return;
    var status = q.root.querySelector('.sq-status');
    var finish = function (msg, method) {
      q.submitted = true;
      try { sessionStorage.removeItem(STORE_KEY); } catch (e) {}
      /* generate_lead — the single point all three successful delivery paths (worker, worker-
         rejected-so-rescued-via-contact-form, no-worker-configured-so-contact-form-fallback)
         converge on. Which EVENT each path reports now depends on whether the worker holds the
         lead — see below. generate_lead is GA4's recommended name, so it can be marked a Key
         Event in GA4 admin; lead_submit_attempt deliberately is not one. */
      if (window.sibTrack && !alreadyTracked(q.submissionId)) {
        markTracked(q.submissionId);
        /* Only the contact-form paths still report a conversion from the browser. When the worker
           took the lead it reports generate_lead itself, from the point the row is actually
           STORED — firing it here as well would count every quiz lead twice. Downgrading this to
           an attempt keeps something genuinely useful: the gap between attempts and server-
           confirmed leads is exactly the silent-failure signal that went unnoticed for weeks.
           The rescue and fallback leads never reach the worker, so nothing server-side will ever
           report them and they must stay generate_lead or they stop being counted at all. */
        var m = method || 'worker';
        window.sibTrack(m === 'worker' ? 'lead_submit_attempt' : 'generate_lead',
          { lead_source: 'quiz', lead_method: m, transaction_id: q.submissionId });
      }
      q.showReport(msg);           /* answers stay in memory for the report render */
      /* Hand the lead forward before the PII is dropped, so /pages/contact can greet them and go
         straight to booking instead of presenting a blank seven-field form to someone who has
         just answered thirteen questions. Best-effort: a failure here must never touch the
         submit path, which has already succeeded. */
      try {
        var ha = q.answers;
        sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({
          name: q.pii.name.trim(),
          email: q.pii.email.trim(),
          phone: q.pii.phone.trim(),
          homeType: optLabel(q.opts('homeTypes'), ha.homeType),
          budget: optLabel(q.opts('budgetBands'), ha.budgetBand),
          location: optLabel(q.opts('locations'), ha.location),
          property: ha.propertyName.trim(),
          sqft: ha.sqft,
          services: ha.services.map(function (v) { return optLabel(q.opts('services'), v); }).join(' + '),
          at: Date.now()
        }));
      } catch (e) {}
      q.pii = { name: '', email: '', phone: '', marketingOptIn: false };
    };
    var fail = function () {
      q.submitting = false;
      btn.disabled = false; btn.textContent = q.t('contact_submit', 'See my estimate →');
      status.className = 'sq-status sq-error';
      status.textContent = q.t('error_label', 'We couldn’t send your estimate just now. Please try again.');
      q.announce(status.textContent);
    };
    /* A 4xx (other than 429) means the worker REJECTED this specific payload — it validated the
       answers and said no. That verdict is deterministic: every retry fails identically, and the
       rejection happens before the lead is ever written to the database, so "please try again"
       loses a real, fully-completed lead for good. This is exactly how a Wet-kitchen submission
       vanished on 2026-08-30, when the room key existed in this file but not in the worker's copy
       of sib-estimator.js. Never let a schema disagreement between the two cost us the lead:
       hand it to the studio through the Shopify contact form, the same path used when no worker
       is configured at all. 429 is excluded — rate limiting genuinely is worth retrying. */
    var rescueViaContactForm = function () {
      return q.sendViaShopifyContact().then(function (c) {
        if (!c.ok) throw new Error('contact fallback failed');
        finish(q.t('success_pending', 'Your details are with our studio — we’ll follow up with your emailed copy shortly.'), 'contact_rescue');
      });
    };
    /* Recorded BEFORE the POST, deliberately. The funnel previously ended at 'contact' — "reached
       the contact screen" — which most visitors abandon, so comparing that against saved rows
       yields a large permanent gap that is almost entirely legitimate abandonment, and any alert
       built on it gets muted in week one. 'submitted' means a human pressed the button, so a
       'submitted' beacon with no matching row is unambiguously a lost lead. This is what makes
       reconciliation possible at all, and it would have caught the original incident outright. */
    q.trackStep('submitted', QUIZ_SUBMITTED_STEP);

    q.submitting = true;
    btn.disabled = true; btn.textContent = q.t('loading_label', 'Preparing your estimate…');
    status.className = 'sq-status'; status.textContent = '';

    /* Only a real http(s) endpoint counts — anything else must not break the flow. */
    var endpoint = /^https?:\/\//i.test(q.cfg.endpoint || '') ? q.cfg.endpoint : '';
    if (!endpoint) {
      /* No worker yet: deliver the lead to the studio via Shopify, then show the report. */
      q.sendViaShopifyContact().then(function (r) {
        if (!r.ok) throw new Error('contact form failed');
        finish(q.t('success_pending', 'Your details are with our studio — we’ll follow up with your emailed copy shortly.'), 'contact_fallback');
      }).catch(fail);
      return;
    }
    var st = q.estimateState();
    var payload = {
      existingSiteCondition: q.answers.existingSiteCondition,
      projectPurpose: q.answers.projectPurpose,
      homeType: q.answers.homeType,
      sqft: q.answers.sqft,
      projectRooms: q.answers.projectRooms,
      roomCounts: q.answers.roomCounts,
      propertyName: q.answers.propertyName,
      location: q.answers.location,
      services: q.answers.services,
      budgetBand: q.answers.budgetBand,
      /* Normalise, never send the raw answer. Two distinct live bugs both land here:
         (1) Styling/procurement-only visitors skip the contractor screen entirely, so the
             answer is still the initial '' — which the worker rejects outright (422 "invalid
             contractor preference"), losing the lead after the visitor has typed their email.
         (2) A visitor can answer "Yes, include an estimate", go Back, and switch to styling
             only. The stale 'include' would then tell the worker to price renovation works
             that the on-page report has correctly hidden, so the emailed copy contradicts
             what they just read.
         'none' is both valid and true for these services: they put no contractor on site. */
      contractorPref: q.contractorApplies() ? q.answers.contractorPref : 'none',
      tier: q.answers.tier,
      works: q.answers.works,
      name: q.pii.name.trim(),
      email: q.pii.email.trim(),
      phone: q.pii.phone.trim(),
      marketingOptIn: q.pii.marketingOptIn,
      calculatorState: st,
      calculatorUrl: q.calculatorURL(),
      emailCopy: q.cfg.emailCopy || {},
      submissionId: q.submissionId
    };
    /* Attribution rides along with the submission. Without it no lead can be credited to a
       channel, no channel can be given a cost per lead, and paid spend is unmeasurable. */
    try {
      var attr = readAttribution();
      payload.source = attr.source;
      payload.landingPage = attr.landingPage;
      payload.referrer = attr.referrer;
      if (attr.campaign) payload.campaign = attr.campaign;
    } catch (e) {}
    /* GA4 identity, so the worker's server-side generate_lead joins THIS visit instead of
       inventing a new user and reporting every conversion as (direct)/(none). Read straight from
       Google's own cookies — _ga carries "GA1.1.<client_id>", _ga_<CONTAINER> carries
       "GS1.1.<session_id>.<n>...". Both are first-party and already on this domain; nothing new
       is set or collected here. Silent if GA4 has not loaded or the visitor blocks it — the lead
       still sends, it just arrives unattributed and flagged as such. */
    try {
      var gc = document.cookie.match(/(?:^|;\s*)_ga=GA\d+\.\d+\.([\d.]+)/);
      if (gc) payload.gaClientId = gc[1];
      /* GS2 writes "GS2.1.s<session_id>$o1$g1$..."; GS1 wrote "GS1.1.<session_id>.<n>". The
         optional `s` covers both, so this keeps working across Google's next format bump. */
      var gs = document.cookie.match(/(?:^|;\s*)_ga_[A-Z0-9]+=GS\d+\.\d+\.s?(\d+)/);
      if (gs) payload.gaSessionId = gs[1];
    } catch (e) {}
    var body = JSON.stringify(payload);

    /* Last-gasp on unload. An in-flight fetch dies with the page context, so a visitor who closes
       or backgrounds the tab mid-submit used to lose the lead outright with no trace anywhere.
       sendBeacon is the only request that survives unload. It carries the SAME submissionId, so it
       is the same submission being completed, not a second one. Registered once per element. */
    q.__pending = body;
    if (!q.__gaspBound) {
      q.__gaspBound = true;
      var lastGasp = function () {
        if (q.submitted || q.__gasped || !q.__pending) return;
        q.__gasped = true;
        try {
          if (navigator.sendBeacon) navigator.sendBeacon(endpoint, new Blob([q.__pending], { type: 'application/json' }));
        } catch (e) {}
      };
      window.addEventListener('pagehide', lastGasp);
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') lastGasp();
      });
    }

    /* One bounded attempt. Without a timeout a hung worker span leaves the visitor watching a
       spinner forever; AbortController is what turns that into a retryable failure. */
    var TIMEOUT_MS = 15000, MAX_ATTEMPTS = 3, attemptNo = 0;
    var wait = function (ms) { return new Promise(function (r) { setTimeout(r, ms); }); };
    var backoff = function (n) { return Math.round((n === 1 ? 600 : 1600) * (0.75 + Math.random() * 0.5)); };

    var attempt = function () {
      var ctl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      var timer = ctl ? setTimeout(function () { ctl.abort(); }, TIMEOUT_MS) : null;
      var opts = { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body };
      if (ctl) opts.signal = ctl.signal;
      return fetch(endpoint, opts).then(function (res) {
        if (timer) clearTimeout(timer);
        return res.json().catch(function () { return {}; }).then(function (j) {
          /* SUCCESS IS `saved`, NOT `ok`. The worker reports two independent facts: whether the
             lead is stored (saved) and whether the customer's copy went out (emailed). Treating a
             bare ok:true as success is what showed "A copy is on its way to your inbox" for a lead
             that was never written — the idempotency short-circuit returns {ok:true,duplicate:true}
             having saved nothing. A duplicate is therefore NOT success here: we cannot tell whether
             the original attempt stored anything, and a duplicate lead is recoverable where a lost
             one is not. The `!j.duplicate` legacy clause only covers a worker deployed before the
             saved/emailed contract; remove it once that is everywhere. */
          var saved = (j.saved === true) ||
                      (j.saved === undefined && res.ok && j.ok === true && !j.duplicate);
          return { saved: saved, emailed: j.emailed === true, status: res.status, error: j && j.error };
        });
      }, function (e) { if (timer) clearTimeout(timer); throw e; });
    };

    var giveToStudio = function (why) {
      if (window.console && console.warn) console.warn('sib-quiz: ' + why + ' — handing the lead to the studio via the contact form');
      return rescueViaContactForm();
    };

    /* Every terminal outcome ends with the lead somewhere a human will see it. Previously only a
       4xx reached the rescue, so the entire transient class — worker down, DNS blip, tunnel drop,
       request timeout, CORS mismatch — showed "please try again" and lost the lead the moment the
       visitor walked away. That is the most common failure in the real world, and it was the one
       with no safety net. */
    var run = function () {
      attemptNo++;
      return attempt().then(function (r) {
        if (r.saved) {
          q.__pending = null;
          finish(r.emailed === false
            ? q.t('success_pending', 'Your details are with our studio — we’ll follow up with your emailed copy shortly.')
            : q.t('success_label', 'A copy is on its way to your inbox.'));
          return;
        }
        /* Deterministic rejection: the worker validated this payload and said no. Every retry
           fails identically, and it happens before the row is written. */
        if (r.status >= 400 && r.status < 500 && r.status !== 429) return giveToStudio('worker rejected the submission (' + r.status + ' ' + (r.error || 'unknown') + ')');
        /* Rate limited. Retry-After is measured in minutes — far longer than anyone will sit and
           wait — so route straight to the studio rather than stalling or spinning. */
        if (r.status === 429) return giveToStudio('rate limited');
        if (attemptNo < MAX_ATTEMPTS) return wait(backoff(attemptNo)).then(run);
        return giveToStudio('worker did not confirm the lead was saved after ' + attemptNo + ' attempts');
      }, function (err) {
        /* Network failure, CORS, DNS, or our own timeout. Indistinguishable from here, all retryable. */
        if (attemptNo < MAX_ATTEMPTS) return wait(backoff(attemptNo)).then(run);
        return giveToStudio('could not reach the worker after ' + attemptNo + ' attempts (' + ((err && err.name) || 'error') + ')');
      });
    };

    run().catch(fail);
  };

  if (!customElements.get('sib-quiz')) customElements.define('sib-quiz', SibQuiz);
  /* Exported for the Node test-suite so it exercises the SHIPPED implementations rather than
     a mirror that can silently drift out of step with them. */
  return { validators: validators, KEYS: KEYS, OPTS: OPTS,
    idStillReusable: idStillReusable, ID_REUSE_MAX_MS: ID_REUSE_MAX_MS,
    contractorAppliesTo: contractorAppliesTo, RENOVATION_CAPABLE_SERVICES: RENOVATION_CAPABLE_SERVICES,
    segsToHtml: segsToHtml, beatsToHtml: beatsToHtml, beatsToText: beatsToText,
    BUDGET_RANGE: BUDGET_RANGE, budgetPhrase: budgetPhrase,
    TIER_LABEL: TIER_LABEL, TIER_ENGINE_NAME: TIER_ENGINE_NAME, TIER_ORDER: TIER_ORDER };
}));


